import { Controller } from '@hotwired/stimulus'
import fetchWithHandlingLoginRedirect from '../modules/utils/fetchWithHandlingLoginRedirect'
import {
  trans, ADD_FIELD_FAILED, REORDER_FAILED, UNDO_FAILED, REDO_FAILED, UPDATE_FIELD_FAILED,
  DUPLICATE_FIELD_FAILED, REMOVE_FIELD_FAILED, ADD_SECTION_FAILED, RENAME_SECTION_FAILED,
  DELETE_SECTION_FAILED, MOVE_SECTION_FAILED, MERGE_SECTION_FAILED, PROPERTIES_PANE_HEAD,
  PROPERTIES_PANE_HEAD_QUESTION, PROPERTIES_PANE_HEAD_SECTION, PROPERTIES_PANE_HEAD_TEXT_BLOCK, DELETE_SECTION_TITLE,
  DELETE_SECTION_BULLET_GOES, DELETE_SECTION_BULLET_UNCHANGED, DELETE_SECTION_BULLET_FIELDS_ONE,
  DELETE_SECTION_BULLET_FIELDS_MANY, DELETE_SECTION_BULLET_MERGE_INSTEAD,
  PROPERTIES_PANE_HEAD_GROUP, QUESTIONS_REPEATED_ONE, QUESTIONS_REPEATED_MANY,
  ADD_CHILD_QUESTION_FAILED, REORDER_CHILDREN_FAILED, REMOVE_GROUP_FAILED, REMOVE_GROUP_TITLE,
  REMOVE_GROUP_BULLET_QUESTIONS_ONE, REMOVE_GROUP_BULLET_QUESTIONS_MANY, REMOVE_GROUP_BULLET_LOSE_REPEAT,
  CONDITION_NO_ELIGIBLE_PARENT, CONDITION_CAPPED, ALWAYS_SHOWN, PROPERTIES_PANE_HEAD_PAGE_BREAK,
  OPTIONS, OPTIONS_AS_COLUMNS, SECTION_CONDITION_NO_ELIGIBLE_PARENT, MOVE_FIELD_FAILED,
  YES, NO, NOT_APPLICABLE, DATE_ONLY,
  FIELD_KIND_NAME, FIELD_KIND_CONTACT, FIELD_KIND_EMAIL, FIELD_KIND_PHONE, FIELD_KIND_POSTCODE, FIELD_KIND_ORGANISATION,
  FIELD_SUMMARY_MAX_LENGTH, FIELD_SUMMARY_OPTION_ONE, FIELD_SUMMARY_OPTION_MANY,
  FIELD_SUMMARY_ROW_ONE, FIELD_SUMMARY_ROW_MANY, FIELD_SUMMARY_RANGE, FIELD_SUMMARY_RANGE_ANY,
  FIELD_SUMMARY_NOT_APPLICABLE, FIELD_SUMMARY_MULTIPLE_MEDIA, FIELD_SUMMARY_ANNOTATION,
  FIELD_SUMMARY_MULTIPLE_ANSWERS
} from '../translator'

declare const $: (selector: Element) => { modal: (action: string) => void }

const XHR_HEADERS = { 'X-Requested-With': 'XMLHttpRequest' }
const DELETE_SECTION_MODAL_ID = 'ftDeleteSectionModal'
const DELETE_GROUP_MODAL_ID = 'ftDeleteGroupModal'

const QUESTION_SELECTOR = '.ft-question'
const SECTION_SELECTOR = '.ft-section'
const GROUP_BODY_SELECTOR = '.ft-group__body'
const DRAGGING_CLASS = 'ft-question--dragging'
const DROP_TARGET_CLASS = 'ft-question--drop-target'
const ADD_ROW_DROP_TARGET_CLASS = 'ft-addrow--drop-target'
const SECTION_DRAGGING_CLASS = 'ft-section--dragging'
const SECTION_DROP_TARGET_CLASS = 'ft-section--drop-target'
const SHEET_OPEN_CLASS = 'ft-pane--sheet-open'
const DRAG_ACTIVATION_THRESHOLD_PX = 6
const CLICK_SUPPRESSION_WINDOW_MS = 400
const EDGE_SCROLL_ZONE_PX = 90
const EDGE_SCROLL_STEP_PX = 14
const SELECTED_CLASS = 'ft-question--selected'
const SECTION_SELECTED_CLASS = 'ft-section__label--selected'
const SHORT_TEXT_TYPE = 'short-text'
const LONG_TEXT_TYPE = 'long-text'
const PAGE_BREAK_TYPE = 'page-break'
const TEXT_BLOCK_TYPE = 'static-text'
const NUMBER_TYPE = 'number'
const DATE_TIME_TYPE = 'datetime'
const MEDIA_TYPE = 'upload-media'
const GROUP_TYPE = 'repeating-group'
const YES_NO_TYPE = 'yes-no'
const CHOICE_TYPE = 'choice'
const GRID_DISPLAY = 'grid'
const MAX_CONDITION_DEPTH = 2
const PLACEHOLDER = '00000000-0000-0000-0000-000000000000'

const FIELD_KIND_LABELS: Record<string, typeof FIELD_KIND_NAME> = {
  name: FIELD_KIND_NAME,
  contact: FIELD_KIND_CONTACT,
  email: FIELD_KIND_EMAIL,
  phone: FIELD_KIND_PHONE,
  postcode: FIELD_KIND_POSTCODE,
  organisation: FIELD_KIND_ORGANISATION
}

interface AddedField {
  id: string
  name: string
  icon: string
  type_label: string
  description?: string
  type?: string
  kind?: string
  display?: string
  min?: number | null
  max?: number | null
  max_length?: number | null
  is_date_only?: boolean
  allow_not_applicable?: boolean
  allow_multiple_media?: boolean
  allow_annotation?: boolean
  allow_multiple_answers?: boolean
  options?: string[]
  rows?: string[]
  is_optional?: boolean
  is_lead_field?: boolean
  children?: AddedField[]
  condition_parent_field_id?: string | null
  condition_answer?: string | null
}

interface AddFieldResponse {
  result: { field: AddedField }
}

interface UpdatedField {
  id: string
  name: string
  description: string
  icon?: string
  type_label?: string
  type?: string
  kind: string
  display?: string
  min: number | null
  max: number | null
  max_length?: number | null
  is_date_only: boolean
  allow_not_applicable?: boolean
  allow_multiple_media: boolean
  allow_annotation: boolean
  allow_multiple_answers: boolean
  options: string[]
  rows?: string[]
  is_optional: boolean
  is_lead_field: boolean
  condition_parent_field_id: string | null
  condition_answer: string | null
}

interface DependentConditionResult {
  kind: 'field' | 'section'
  id: string
  condition_parent_field_id: string | null
  condition_answer: string | null
}

interface UpdateFieldResponse {
  result: { field: UpdatedField, dependents?: DependentConditionResult[] }
}

interface SummaryField {
  type?: string
  kind?: string
  max_length?: number | null
  display?: string
  options?: string[]
  rows?: string[]
  allow_not_applicable?: boolean
  min?: number | null
  max?: number | null
  is_date_only?: boolean
  allow_multiple_media?: boolean
  allow_annotation?: boolean
  allow_multiple_answers?: boolean
  description?: string
}

interface AddedSection {
  id: string
  name: string
  condition_parent_field_id?: string | null
  condition_answer?: string | null
}

interface AddSectionResponse {
  result: { section: AddedSection }
}

interface MergedSection {
  id: string
  name: string
  fields: AddedField[]
  condition_parent_field_id?: string | null
  condition_answer?: string | null
  dependents?: DependentConditionResult[]
}

interface MergeSectionResponse {
  result: { section: MergedSection, merged_section_id: string }
}

interface RestoreSectionResponse {
  result: { section: MergedSection }
}

interface RestoreFieldResponse {
  result: { field: AddedField, dependents: DependentConditionResult[] }
}

interface AddOperation {
  kind: 'add'
  type: string
  sectionId: string
  fieldId: string
}

interface ReorderOperation {
  kind: 'reorder'
  sectionId: string
  before: string[]
  after: string[]
}

type DropTarget = { kind: 'row', row: HTMLElement, isAfter: boolean } | { kind: 'addRow', addRow: HTMLElement }
type SectionDropTarget = { section: HTMLElement, isAfter: boolean }

interface MoveFieldOperation {
  kind: 'moveField'
  fieldIds: string[]
  sourceSectionId: string
  sourceIndex: number
  destSectionId: string
  destIndex: number
}

interface FieldSnapshot {
  type: string
  name: string
  description: string
  kind: string
  display: string
  min: string
  max: string
  maxLength: string
  isDateOnly: boolean
  allowNotApplicable: boolean
  allowMultipleMedia: boolean
  allowAnnotation: boolean
  allowMultipleAnswers: boolean
  options: string[]
  rows: string[]
  isRequired: boolean
  isLeadField: boolean
  conditionParentFieldId: string
  conditionAnswer: string
}

interface DependentCondition {
  kind: 'field' | 'section'
  id: string
  parentId: string
  answer: string
}

interface RemoveOperation {
  kind: 'remove'
  fieldId: string
  sectionId: string
  index: number
  snapshot: FieldSnapshot
  dependents: DependentCondition[]
}

interface ConditionState {
  parentId: string
  answer: string
  sectionId: string
  order: string[]
}

interface ConditionOperation {
  kind: 'condition'
  fieldId: string
  before: ConditionState
  after: ConditionState
}

interface DuplicateOperation {
  kind: 'duplicate'
  sourceFieldId: string
  fieldId: string
}

interface AddSectionOperation {
  kind: 'addSection'
  sectionId: string
}

interface RemoveSectionOperation {
  kind: 'removeSection'
  sectionId: string
  name: string
  index: number
  fields: MergedFieldRecord[]
  conditionParentFieldId: string
  conditionAnswer: string
  dependents: DependentCondition[]
}

interface MergedFieldRecord {
  fieldId: string
  snapshot: FieldSnapshot
}

interface MergeSectionOperation {
  kind: 'mergeSection'
  sectionId: string
  name: string
  index: number
  aboveSectionId: string
  fieldIds: string[]
}

interface RemoveGroupOperation {
  kind: 'removeGroup'
  groupId: string
  sectionId: string
  name: string
  index: number
  children: MergedFieldRecord[]
  conditionParentFieldId: string
  conditionAnswer: string
}

interface AddChildOperation {
  kind: 'addChild'
  groupId: string
  type: string
  fieldId: string
}

interface RemoveChildOperation {
  kind: 'removeChild'
  groupId: string
  fieldId: string
  index: number
  snapshot: FieldSnapshot
}

interface ReorderChildrenOperation {
  kind: 'reorderChildren'
  groupId: string
  before: string[]
  after: string[]
}

type Operation = AddOperation | ReorderOperation | MoveFieldOperation | RemoveOperation | DuplicateOperation
  | AddSectionOperation | RemoveSectionOperation | MergeSectionOperation
  | RemoveGroupOperation | AddChildOperation | RemoveChildOperation | ReorderChildrenOperation
  | ConditionOperation

export default class FormBuilderController extends Controller<HTMLElement> {
  static targets = [
    'sectionList', 'section', 'sectionRowTemplate',
    'rowTemplate', 'childRowTemplate', 'groupRowTemplate', 'error',
    'propertiesEmpty', 'propertiesForm', 'propertiesPageBreakForm', 'propertiesLabel', 'propertiesDescription',
    'propertiesType', 'propertiesTypeGroup',
    'propertiesDescriptionGroup', 'propertiesTextBlockGroup', 'propertiesTextBlock', 'propertiesRequiredGroup',
    'propertiesKindGroup', 'propertiesKind',
    'propertiesDisplayGroup', 'propertiesDisplay',
    'propertiesChoiceGroup', 'propertiesAllowMultipleAnswers',
    'propertiesRangeGroup', 'propertiesMin', 'propertiesMax',
    'propertiesMaxLengthGroup', 'propertiesMaxLength',
    'propertiesDateOnlyGroup', 'propertiesDateOnly',
    'propertiesYesNoGroup', 'propertiesAllowNotApplicable',
    'propertiesPhotoGroup', 'propertiesAllowMultipleMedia', 'propertiesAllowAnnotation',
    'propertiesOptionsGroup', 'propertiesOptionsList', 'propertiesOptionsLabel', 'optionRowTemplate',
    'propertiesRowsGroup', 'propertiesRowsList', 'rowRowTemplate',
    'propertiesRequired', 'propertiesLeadField', 'propertiesLeadFieldGroup', 'propertiesLeadFieldAccessNote',
    'propertiesConditionGroup', 'propertiesConditionParent', 'propertiesConditionAnswer', 'propertiesConditionEmptyNote',
    'propertiesSectionForm', 'propertiesSectionName',
    'propertiesSectionConditionGroup', 'propertiesSectionConditionParent', 'propertiesSectionConditionAnswer',
    'propertiesSectionConditionEmptyNote', 'propertiesSectionConditionNote',
    'propertiesGroupForm', 'propertiesGroupName', 'propertiesHead',
    'paletteSheet', 'propertiesSheet', 'sheetBackdrop',
    'propertiesGroupConditionGroup', 'propertiesGroupConditionParent', 'propertiesGroupConditionAnswer', 'propertiesGroupConditionEmptyNote'
  ]

  static values = {
    addFieldUrlTemplate: String,
    reorderFieldsUrlTemplate: String,
    moveFieldsUrlTemplate: String,
    deleteUrlTemplate: String,
    updateUrlTemplate: String,
    duplicateUrlTemplate: String,
    addSectionUrl: String,
    updateSectionUrlTemplate: String,
    deleteSectionUrlTemplate: String,
    mergeSectionUrlTemplate: String,
    restoreSectionUrl: String,
    unmergeSectionUrl: String,
    reorderSectionsUrl: String,
    addChildFieldUrlTemplate: String,
    reorderChildFieldsUrlTemplate: String,
    restoreGroupUrlTemplate: String,
    restoreFieldUrlTemplate: String,
    restoreChildFieldUrlTemplate: String,
    restoreConditionUrlTemplate: String,
    accessMode: String
  }

  declare readonly sectionListTarget: HTMLElement
  declare readonly sectionTargets: HTMLElement[]
  declare readonly sectionRowTemplateTarget: HTMLTemplateElement
  declare readonly rowTemplateTarget: HTMLTemplateElement
  declare readonly childRowTemplateTarget: HTMLTemplateElement
  declare readonly groupRowTemplateTarget: HTMLTemplateElement
  declare readonly errorTarget: HTMLElement
  declare readonly hasErrorTarget: boolean
  declare readonly propertiesEmptyTarget: HTMLElement
  declare readonly propertiesFormTarget: HTMLElement
  declare readonly propertiesPageBreakFormTarget: HTMLElement
  declare readonly propertiesLabelTarget: HTMLInputElement
  declare readonly propertiesDescriptionTarget: HTMLInputElement
  declare readonly propertiesTypeTarget: HTMLSelectElement
  declare readonly propertiesTypeGroupTarget: HTMLElement
  declare readonly propertiesDescriptionGroupTarget: HTMLElement
  declare readonly propertiesTextBlockGroupTarget: HTMLElement
  declare readonly propertiesTextBlockTarget: HTMLTextAreaElement
  declare readonly propertiesRequiredGroupTarget: HTMLElement
  declare readonly propertiesKindGroupTarget: HTMLElement
  declare readonly propertiesKindTarget: HTMLSelectElement
  declare readonly propertiesDisplayGroupTarget: HTMLElement
  declare readonly propertiesDisplayTarget: HTMLSelectElement
  declare readonly propertiesChoiceGroupTarget: HTMLElement
  declare readonly propertiesAllowMultipleAnswersTarget: HTMLInputElement
  declare readonly propertiesRangeGroupTarget: HTMLElement
  declare readonly propertiesMinTarget: HTMLInputElement
  declare readonly propertiesMaxTarget: HTMLInputElement
  declare readonly propertiesMaxLengthGroupTarget: HTMLElement
  declare readonly propertiesMaxLengthTarget: HTMLInputElement
  declare readonly propertiesDateOnlyGroupTarget: HTMLElement
  declare readonly propertiesDateOnlyTarget: HTMLSelectElement
  declare readonly propertiesYesNoGroupTarget: HTMLElement
  declare readonly propertiesAllowNotApplicableTarget: HTMLInputElement
  declare readonly propertiesPhotoGroupTarget: HTMLElement
  declare readonly propertiesAllowMultipleMediaTarget: HTMLInputElement
  declare readonly propertiesAllowAnnotationTarget: HTMLInputElement
  declare readonly propertiesOptionsGroupTarget: HTMLElement
  declare readonly propertiesOptionsListTarget: HTMLElement
  declare readonly propertiesOptionsLabelTarget: HTMLElement
  declare readonly optionRowTemplateTarget: HTMLTemplateElement
  declare readonly propertiesRowsGroupTarget: HTMLElement
  declare readonly propertiesRowsListTarget: HTMLElement
  declare readonly rowRowTemplateTarget: HTMLTemplateElement
  declare readonly propertiesRequiredTarget: HTMLInputElement
  declare readonly propertiesLeadFieldTarget: HTMLInputElement
  declare readonly propertiesLeadFieldGroupTarget: HTMLElement
  declare readonly propertiesLeadFieldAccessNoteTarget: HTMLElement
  declare readonly propertiesConditionGroupTarget: HTMLElement
  declare readonly propertiesConditionParentTarget: HTMLSelectElement
  declare readonly propertiesConditionAnswerTarget: HTMLSelectElement
  declare readonly propertiesConditionEmptyNoteTarget: HTMLElement
  declare readonly propertiesSectionFormTarget: HTMLElement
  declare readonly propertiesSectionNameTarget: HTMLInputElement
  declare readonly propertiesSectionConditionGroupTarget: HTMLElement
  declare readonly propertiesSectionConditionParentTarget: HTMLSelectElement
  declare readonly propertiesSectionConditionAnswerTarget: HTMLSelectElement
  declare readonly propertiesSectionConditionEmptyNoteTarget: HTMLElement
  declare readonly propertiesSectionConditionNoteTarget: HTMLElement
  declare readonly propertiesGroupFormTarget: HTMLElement
  declare readonly propertiesGroupNameTarget: HTMLInputElement
  declare readonly propertiesGroupConditionGroupTarget: HTMLElement
  declare readonly propertiesGroupConditionParentTarget: HTMLSelectElement
  declare readonly propertiesGroupConditionAnswerTarget: HTMLSelectElement
  declare readonly propertiesGroupConditionEmptyNoteTarget: HTMLElement
  declare readonly propertiesHeadTarget: HTMLElement
  declare readonly paletteSheetTarget: HTMLElement
  declare readonly propertiesSheetTarget: HTMLElement
  declare readonly sheetBackdropTarget: HTMLElement
  declare readonly addFieldUrlTemplateValue: string
  declare readonly reorderFieldsUrlTemplateValue: string
  declare readonly moveFieldsUrlTemplateValue: string
  declare readonly deleteUrlTemplateValue: string
  declare readonly updateUrlTemplateValue: string
  declare readonly duplicateUrlTemplateValue: string
  declare readonly addSectionUrlValue: string
  declare readonly updateSectionUrlTemplateValue: string
  declare readonly deleteSectionUrlTemplateValue: string
  declare readonly mergeSectionUrlTemplateValue: string
  declare readonly restoreSectionUrlValue: string
  declare readonly unmergeSectionUrlValue: string
  declare readonly reorderSectionsUrlValue: string
  declare readonly addChildFieldUrlTemplateValue: string
  declare readonly reorderChildFieldsUrlTemplateValue: string
  declare readonly restoreGroupUrlTemplateValue: string
  declare readonly restoreFieldUrlTemplateValue: string
  declare readonly restoreChildFieldUrlTemplateValue: string
  declare readonly restoreConditionUrlTemplateValue: string
  declare readonly accessModeValue: string

  private dragged: HTMLElement | null = null
  private dragStartPos: { x: number, y: number } | null = null
  private dragActive = false
  private dragGhost: HTMLElement | null = null
  private dropTarget: DropTarget | null = null
  private dragEndedAt = 0
  private draggedSection: HTMLElement | null = null
  private sectionDragStartPos: { x: number, y: number } | null = null
  private sectionDragActive = false
  private sectionDragGhost: HTMLElement | null = null
  private sectionDropTarget: SectionDropTarget | null = null
  private done: Operation[] = []
  private undone: Operation[] = []
  private selectedFieldId: string | null = null
  private selectedSectionId: string | null = null
  private cancellingSectionName = false
  private pendingDeleteSection: { sectionId: string, name: string, index: number, fields: MergedFieldRecord[], conditionParentFieldId: string, conditionAnswer: string } | null = null
  private pendingRemoveGroup: { groupId: string, sectionId: string, name: string, index: number, children: MergedFieldRecord[], conditionParentFieldId: string, conditionAnswer: string } | null = null
  private pendingPropertiesSave: Promise<void> | null = null

  connect (): void {
    this.refreshConditionDepths()
  }

  async add (event: Event): Promise<void> {
    const item = event.currentTarget as HTMLElement
    const type = item.dataset.fieldType
    const display = item.dataset.fieldDisplay ?? ''
    const target = this.insertTargetForAdd()

    if (!type || !target) {
      return
    }

    const { section, anchorRow } = target

    this.clearError()

    const field = await this.addField(type, section.dataset.sectionId ?? '', display)

    if (!field) {
      this.showError(trans(ADD_FIELD_FAILED, {}, 'form-types'))

      return
    }

    const row = this.appendRowToSection(section, field)

    if (row && anchorRow) {
      anchorRow.after(row)
      await this.persistFieldOrder(section)
    }

    this.record({ kind: 'add', type, sectionId: section.dataset.sectionId ?? '', fieldId: field.id })
  }

  async addQuestion (event: Event): Promise<void> {
    await this.quickAdd(event, SHORT_TEXT_TYPE)
  }

  async addPageBreak (event: Event): Promise<void> {
    await this.quickAdd(event, PAGE_BREAK_TYPE)
  }

  private async quickAdd (event: Event, type: string): Promise<void> {
    const section = this.sectionFrom(event)

    if (!section) {
      return
    }

    this.clearError()

    const field = await this.addField(type, section.dataset.sectionId ?? '')

    if (!field) {
      this.showError(trans(ADD_FIELD_FAILED, {}, 'form-types'))

      return
    }

    this.appendRowToSection(section, field)
    this.record({ kind: 'add', type, sectionId: section.dataset.sectionId ?? '', fieldId: field.id })
  }

  async addChildQuestion (event: Event): Promise<void> {
    const button = event.currentTarget as HTMLElement
    const groupId = button.dataset.groupId

    if (!groupId) {
      return
    }

    this.clearError()

    const field = await this.addChildFieldRequest(groupId, SHORT_TEXT_TYPE)

    if (!field) {
      this.showError(trans(ADD_CHILD_QUESTION_FAILED, {}, 'form-types'))

      return
    }

    this.appendChildRow(groupId, field)
    this.record({ kind: 'addChild', groupId, type: SHORT_TEXT_TYPE, fieldId: field.id })
  }

  private async addChildFieldRequest (groupId: string, type: string, id?: string): Promise<AddedField | null> {
    try {
      const response = await fetchWithHandlingLoginRedirect(
        this.addChildFieldUrlTemplateValue.replace(PLACEHOLDER, groupId),
        {
          method: 'POST',
          headers: { ...XHR_HEADERS, 'Content-Type': 'application/json' },
          body: JSON.stringify({ type, id })
        }
      )

      const body = await response.json() as AddFieldResponse

      return body.result.field
    } catch {
      return null
    }
  }

  private appendChildRow (groupId: string, field: AddedField): HTMLElement | null {
    const group = this.findRow(groupId)
    const body = group?.querySelector<HTMLElement>(GROUP_BODY_SELECTOR)

    if (!group || !body) {
      return null
    }

    const row = this.buildRow(field, true)

    if (!row) {
      return null
    }

    const addRow = body.querySelector('.ft-addrow')

    if (addRow) {
      addRow.before(row)
    } else {
      body.appendChild(row)
    }

    this.updateGroupSubtitle(group, this.childRows(group).length)

    return row
  }

  private updateGroupSubtitle (group: HTMLElement, count: number): void {
    const description = group.querySelector('.ft-question__description')

    if (!description) {
      return
    }

    description.textContent = count === 1
      ? trans(QUESTIONS_REPEATED_ONE, {}, 'form-types')
      : trans(QUESTIONS_REPEATED_MANY, { '%count%': count }, 'form-types')
  }

  private async reorderChildren (group: HTMLElement, move: () => void, focusEvent?: Event): Promise<void> {
    const before = this.childRows(group)
    const beforeIds = before.map(row => row.dataset.fieldId ?? '')
    const groupId = group.dataset.fieldId ?? ''

    this.clearError()
    move()

    if (focusEvent?.currentTarget instanceof HTMLElement) {
      focusEvent.currentTarget.focus()
    }

    const afterIds = this.childRows(group).map(row => row.dataset.fieldId ?? '')

    try {
      await fetchWithHandlingLoginRedirect(this.reorderChildFieldsUrlTemplateValue.replace(PLACEHOLDER, groupId), {
        method: 'POST',
        headers: { ...XHR_HEADERS, 'Content-Type': 'application/json' },
        body: JSON.stringify({ field_ids: afterIds })
      })

      this.record({ kind: 'reorderChildren', groupId, before: beforeIds, after: afterIds })
    } catch {
      this.applyChildOrderToRows(group, beforeIds)
      this.showError(trans(REORDER_CHILDREN_FAILED, {}, 'form-types'))
    }
  }

  private applyChildOrderToRows (group: HTMLElement, fieldIds: string[]): void {
    const body = group.querySelector<HTMLElement>(GROUP_BODY_SELECTOR)

    if (!body) {
      return
    }

    const addRow = body.querySelector('.ft-addrow')

    fieldIds.forEach(fieldId => {
      const row = this.childRows(group).find(candidate => candidate.dataset.fieldId === fieldId)

      if (!row) {
        return
      }

      if (addRow) {
        addRow.before(row)
      } else {
        body.appendChild(row)
      }
    })
  }

  private async applyChildOrder (groupId: string, fieldIds: string[]): Promise<boolean> {
    const group = this.findRow(groupId)

    if (!group) {
      return false
    }

    const previous = this.childRows(group).map(row => row.dataset.fieldId ?? '')

    this.applyChildOrderToRows(group, fieldIds)

    try {
      await fetchWithHandlingLoginRedirect(this.reorderChildFieldsUrlTemplateValue.replace(PLACEHOLDER, groupId), {
        method: 'POST',
        headers: { ...XHR_HEADERS, 'Content-Type': 'application/json' },
        body: JSON.stringify({ field_ids: fieldIds })
      })
    } catch {
      this.applyChildOrderToRows(group, previous)

      return false
    }

    return true
  }

  private async restoreGroupWithChildren (groupId: string, sectionId: string, name: string, index: number, children: MergedFieldRecord[], conditionParentFieldId: string, conditionAnswer: string): Promise<AddedField | null> {
    try {
      const response = await fetchWithHandlingLoginRedirect(
        this.restoreGroupUrlTemplateValue.replace(PLACEHOLDER, sectionId),
        {
          method: 'POST',
          headers: { ...XHR_HEADERS, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: groupId,
            name,
            index,
            conditionParentFieldId,
            conditionAnswer,
            children: children.map(record => ({
              id: record.fieldId,
              type: record.snapshot.type,
              name: record.snapshot.name,
              description: record.snapshot.description,
              kind: record.snapshot.kind,
              display: record.snapshot.display,
              min: record.snapshot.min,
              max: record.snapshot.max,
              maxLength: record.snapshot.maxLength,
              isDateOnly: record.snapshot.isDateOnly,
              allowNotApplicable: record.snapshot.allowNotApplicable,
              allowMultipleMedia: record.snapshot.allowMultipleMedia,
              allowAnnotation: record.snapshot.allowAnnotation,
              allowMultipleAnswers: record.snapshot.allowMultipleAnswers,
              options: record.snapshot.options,
              rows: record.snapshot.rows,
              isRequired: record.snapshot.isRequired
            }))
          })
        }
      )

      const body = await response.json() as AddFieldResponse

      return body.result.field
    } catch {
      return null
    }
  }

  private async restoreChildField (fieldId: string, groupId: string, snapshot: FieldSnapshot, index: number): Promise<string | null> {
    let field: AddedField

    try {
      const response = await fetchWithHandlingLoginRedirect(this.restoreChildFieldUrlTemplateValue.replace(PLACEHOLDER, groupId), {
        method: 'POST',
        headers: { ...XHR_HEADERS, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: fieldId, index, type: snapshot.type, ...this.snapshotBody(snapshot), isLeadField: false })
      })

      field = (await response.json() as AddFieldResponse).result.field
    } catch {
      return null
    }

    this.appendChildRow(groupId, field)

    const group = this.findRow(groupId)

    if (group) {
      const fieldIds = this.childRows(group).map(candidate => candidate.dataset.fieldId ?? '').filter(id => id !== field.id)

      fieldIds.splice(index, 0, field.id)
      this.applyChildOrderToRows(group, fieldIds)
    }

    return field.id
  }

  async addSection (event: Event): Promise<void> {
    const currentSection = this.sectionFrom(event)

    this.clearError()

    const section = await this.createSection()

    if (!section) {
      this.showError(trans(ADD_SECTION_FAILED, {}, 'form-types'))

      return
    }

    const row = this.buildSectionRow(section)

    if (!row) {
      return
    }

    if (currentSection) {
      currentSection.after(row)
    } else {
      this.sectionListTarget.appendChild(row)
    }

    this.record({ kind: 'addSection', sectionId: section.id })
  }

  private async createSection (id?: string): Promise<AddedSection | null> {
    try {
      const response = await fetchWithHandlingLoginRedirect(this.addSectionUrlValue, {
        method: 'POST',
        headers: { ...XHR_HEADERS, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      })

      const body = await response.json() as AddSectionResponse

      return body.result.section
    } catch {
      return null
    }
  }

  selectSection (event: Event): void {
    event.preventDefault()

    if (Date.now() - this.dragEndedAt < CLICK_SUPPRESSION_WINDOW_MS) {
      return
    }

    const section = this.sectionFrom(event)

    if (!section) {
      return
    }

    this.selectSectionElement(section)
  }

  private selectSectionElement (section: HTMLElement): void {
    this.openPropertiesSheet()
    this.allRows().forEach(row => row.classList.remove(SELECTED_CLASS))
    this.selectedFieldId = null

    this.sections().forEach(candidate => candidate.querySelector('.ft-section__label')?.classList.remove(SECTION_SELECTED_CLASS))
    section.querySelector('.ft-section__label')?.classList.add(SECTION_SELECTED_CLASS)
    this.selectedSectionId = section.dataset.sectionId ?? null

    const name = section.querySelector('.ft-section__name')?.textContent ?? ''

    this.propertiesEmptyTarget.classList.add('d-none')
    this.propertiesFormTarget.classList.add('d-none')
    this.propertiesGroupFormTarget.classList.add('d-none')
    this.propertiesPageBreakFormTarget.classList.add('d-none')
    this.propertiesSectionFormTarget.classList.remove('d-none')
    this.propertiesHeadTarget.textContent = trans(PROPERTIES_PANE_HEAD_SECTION, { '%name%': name }, 'form-types')
    this.propertiesSectionNameTarget.value = name

    this.populateSectionConditionParentOptions(
      section,
      this.propertiesSectionConditionParentTarget,
      this.propertiesSectionConditionAnswerTarget,
      this.propertiesSectionConditionEmptyNoteTarget,
      this.propertiesSectionConditionNoteTarget
    )
  }

  editSectionName (event: Event): void {
    event.stopPropagation()
    event.preventDefault()
    this.closeSectionMenu(event)

    const section = this.sectionFrom(event)
    const label = section?.querySelector<HTMLElement>('.ft-section__name')
    const input = section?.querySelector<HTMLInputElement>('.ft-section__name-edit')

    if (!label || !input) {
      return
    }

    input.value = label.textContent ?? ''
    label.classList.add('d-none')
    input.classList.remove('d-none')
    input.focus()
    input.select()
  }

  sectionNameKeydown (event: KeyboardEvent): void {
    const input = event.currentTarget as HTMLInputElement

    if (event.key === 'Enter') {
      input.blur()

      return
    }

    if (event.key === 'Escape') {
      this.cancellingSectionName = true
      input.blur()
    }
  }

  async commitSectionName (event: Event): Promise<void> {
    const cancelled = this.cancellingSectionName

    this.cancellingSectionName = false

    const input = event.currentTarget as HTMLInputElement
    const section = this.sectionFrom(event)
    const label = section?.querySelector<HTMLElement>('.ft-section__name')

    if (!section || !label) {
      return
    }

    input.classList.add('d-none')
    label.classList.remove('d-none')

    if (cancelled) {
      return
    }

    const sectionId = section.dataset.sectionId

    if (!sectionId || input.value === (label.textContent ?? '')) {
      return
    }

    this.clearError()

    const conditionParentFieldId = section.dataset.sectionConditionParentId ?? ''
    const conditionAnswer = section.dataset.sectionConditionAnswer ?? ''

    const result = await this.patchSectionName(sectionId, input.value, conditionParentFieldId, conditionAnswer)

    if (result === null) {
      this.showError(trans(RENAME_SECTION_FAILED, {}, 'form-types'))
      input.value = label.textContent ?? ''

      return
    }

    this.setSectionDisplayName(section, result.name)
    this.applySectionConditionResult(section, result.condition_parent_field_id ?? null, result.condition_answer ?? null)
  }

  async saveSectionName (): Promise<void> {
    const sectionId = this.selectedSectionId
    const section = sectionId ? this.sections().find(candidate => candidate.dataset.sectionId === sectionId) : undefined

    if (!sectionId || !section) {
      return
    }

    const currentName = section.querySelector('.ft-section__name')?.textContent ?? ''
    const currentConditionParentId = section.dataset.sectionConditionParentId ?? ''
    const currentConditionAnswer = section.dataset.sectionConditionAnswer ?? ''

    const unchanged = this.propertiesSectionNameTarget.value === currentName &&
      this.propertiesSectionConditionParentTarget.value === currentConditionParentId &&
      this.propertiesSectionConditionAnswerTarget.value === currentConditionAnswer

    if (unchanged) {
      return
    }

    this.clearError()

    const result = await this.patchSectionName(
      sectionId,
      this.propertiesSectionNameTarget.value,
      this.propertiesSectionConditionParentTarget.value,
      this.propertiesSectionConditionAnswerTarget.value
    )

    if (result === null) {
      this.showError(trans(RENAME_SECTION_FAILED, {}, 'form-types'))
      this.populateSectionConditionParentOptions(
        section,
        this.propertiesSectionConditionParentTarget,
        this.propertiesSectionConditionAnswerTarget,
        this.propertiesSectionConditionEmptyNoteTarget,
        this.propertiesSectionConditionNoteTarget
      )

      return
    }

    this.propertiesSectionNameTarget.value = result.name
    this.setSectionDisplayName(section, result.name)
    this.applySectionConditionResult(section, result.condition_parent_field_id ?? null, result.condition_answer ?? null)
  }

  private async patchSectionName (sectionId: string, name: string, conditionParentFieldId: string, conditionAnswer: string): Promise<AddedSection | null> {
    try {
      const response = await fetchWithHandlingLoginRedirect(
        this.updateSectionUrlTemplateValue.replace(PLACEHOLDER, sectionId),
        {
          method: 'PATCH',
          headers: { ...XHR_HEADERS, 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, conditionParentFieldId, conditionAnswer })
        }
      )

      const body = await response.json() as AddSectionResponse

      this.announceChange()

      return body.result.section
    } catch {
      return null
    }
  }

  private setSectionDisplayName (section: HTMLElement, name: string): void {
    const label = section.querySelector('.ft-section__name')
    const input = section.querySelector<HTMLInputElement>('.ft-section__name-edit')

    if (label) {
      label.textContent = name
    }

    if (input) {
      input.value = name
    }

    if (this.selectedSectionId === section.dataset.sectionId) {
      this.propertiesSectionNameTarget.value = name
      this.propertiesHeadTarget.textContent = trans(PROPERTIES_PANE_HEAD_SECTION, { '%name%': name }, 'form-types')
    }
  }

  private applySectionConditionResult (section: HTMLElement, conditionParentFieldId: string | null, conditionAnswer: string | null): void {
    section.dataset.sectionConditionParentId = conditionParentFieldId ?? ''
    section.dataset.sectionConditionAnswer = conditionAnswer ?? ''

    const tag = section.querySelector('.ft-tag--conditional')

    tag?.classList.toggle('d-none', !conditionParentFieldId)

    if (this.selectedSectionId === section.dataset.sectionId) {
      this.propertiesSectionConditionNoteTarget.classList.toggle('d-none', !conditionParentFieldId)
    }
  }

  deleteSection (event: Event): void {
    event.stopPropagation()
    event.preventDefault()

    const section = this.sectionFrom(event)
    const sectionId = section?.dataset.sectionId

    if (!section || !sectionId) {
      return
    }

    const name = section.querySelector('.ft-section__name')?.textContent ?? ''
    const index = this.sections().indexOf(section)
    const rows = this.rowsInSection(section)
    const fields = rows.map(row => ({ fieldId: row.dataset.fieldId ?? '', snapshot: this.snapshotOf(row) }))
    const conditionParentFieldId = section.dataset.sectionConditionParentId ?? ''
    const conditionAnswer = section.dataset.sectionConditionAnswer ?? ''

    this.pendingDeleteSection = { sectionId, name, index, fields, conditionParentFieldId, conditionAnswer }
    this.closeSectionMenu(event)
    this.showDeleteSectionModal(name, fields.map(field => field.snapshot.name), index)
  }

  private closeSectionMenu (event: Event): void {
    const target = event.currentTarget

    if (!(target instanceof HTMLElement)) {
      return
    }

    const dropdown = target.closest('.dropdown')

    dropdown?.querySelector('.dropdown-menu')?.classList.remove('show')

    const toggle = dropdown?.querySelector<HTMLElement>('.dropdown-toggle')

    toggle?.classList.remove('show')
    toggle?.setAttribute('aria-expanded', 'false')
  }

  private showDeleteSectionModal (name: string, fieldNames: string[], index: number): void {
    const modal = document.getElementById(DELETE_SECTION_MODAL_ID)

    if (!modal) {
      return
    }

    const title = modal.querySelector('.modal-title')

    if (title) {
      title.textContent = trans(DELETE_SECTION_TITLE, { '%name%': name }, 'form-types')
    }

    const points = modal.querySelector('.ft-modal__points')

    if (points) {
      points.innerHTML = ''
      points.appendChild(this.pointItem(trans(DELETE_SECTION_BULLET_GOES, { '%name%': name }, 'form-types')))

      if (fieldNames.length > 0) {
        const fieldsKey = fieldNames.length === 1 ? DELETE_SECTION_BULLET_FIELDS_ONE : DELETE_SECTION_BULLET_FIELDS_MANY

        points.appendChild(this.pointItem(trans(fieldsKey, {
          '%count%': String(fieldNames.length),
          '%names%': fieldNames.join(', ')
        }, 'form-types')))

        if (index > 0) {
          points.appendChild(this.pointItem(trans(DELETE_SECTION_BULLET_MERGE_INSTEAD, {}, 'form-types')))
        }
      }

      points.appendChild(this.pointItem(trans(DELETE_SECTION_BULLET_UNCHANGED, {}, 'form-types')))
    }

    $(modal).modal('show')
  }

  private pointItem (text: string): HTMLLIElement {
    const item = document.createElement('li')

    item.textContent = text

    return item
  }

  async confirmDeleteSection (): Promise<void> {
    const pending = this.pendingDeleteSection

    this.pendingDeleteSection = null

    if (!pending) {
      return
    }

    this.clearError()

    const dependents = this.sectionDependentsOf(pending.sectionId, pending.fields.map(record => record.fieldId))

    if (!await this.deleteSectionById(pending.sectionId)) {
      this.showError(trans(DELETE_SECTION_FAILED, {}, 'form-types'))

      return
    }

    this.record({
      kind: 'removeSection',
      sectionId: pending.sectionId,
      name: pending.name,
      index: pending.index,
      fields: pending.fields,
      conditionParentFieldId: pending.conditionParentFieldId,
      conditionAnswer: pending.conditionAnswer,
      dependents
    })
  }

  private async deleteSectionById (sectionId: string): Promise<boolean> {
    try {
      await fetchWithHandlingLoginRedirect(this.deleteSectionUrlTemplateValue.replace(PLACEHOLDER, sectionId), {
        method: 'DELETE',
        headers: XHR_HEADERS
      })
    } catch {
      return false
    }

    const section = this.sections().find(candidate => candidate.dataset.sectionId === sectionId)
    const fieldIds = section ? this.rowsInSection(section).map(row => row.dataset.fieldId ?? '') : []

    section?.remove()
    fieldIds.forEach(fieldId => this.clearDependentConditionsInDom(fieldId))

    if (this.selectedSectionId === sectionId) {
      this.clearSelection()
    }

    return true
  }

  async mergeSectionAbove (event: Event): Promise<void> {
    event.stopPropagation()
    event.preventDefault()

    const section = this.sectionFrom(event)
    const above = section?.previousElementSibling

    if (!section || !(above instanceof HTMLElement)) {
      return
    }

    const sectionId = section.dataset.sectionId
    const aboveSectionId = above.dataset.sectionId

    if (!sectionId || !aboveSectionId) {
      return
    }

    this.clearError()

    const name = section.querySelector('.ft-section__name')?.textContent ?? ''
    const index = this.sections().indexOf(section)
    const fieldIds = this.rowsInSection(section).map(row => row.dataset.fieldId ?? '')

    const merged = await this.mergeSection(sectionId)

    if (!merged) {
      this.showError(trans(MERGE_SECTION_FAILED, {}, 'form-types'))

      return
    }

    this.replaceSectionFields(above, merged.fields)
    section.remove()

    if (this.selectedSectionId === sectionId) {
      this.clearSelection()
    }

    this.record({ kind: 'mergeSection', sectionId, name, index, aboveSectionId, fieldIds })
  }

  private async mergeSection (sectionId: string): Promise<MergedSection | null> {
    try {
      const response = await fetchWithHandlingLoginRedirect(
        this.mergeSectionUrlTemplateValue.replace(PLACEHOLDER, sectionId),
        { method: 'POST', headers: XHR_HEADERS }
      )

      const body = await response.json() as MergeSectionResponse

      return body.result.section
    } catch {
      return null
    }
  }

  private replaceSectionFields (section: HTMLElement, fields: AddedField[]): void {
    const list = section.querySelector('.ft-questions')

    if (!list) {
      return
    }

    list.innerHTML = ''

    fields.forEach(field => {
      const row = this.buildRow(field)

      if (!row) {
        return
      }

      list.appendChild(row)

      if (field.id === this.selectedFieldId) {
        row.classList.add(SELECTED_CLASS)
      }
    })
  }

  private clearSelection (): void {
    this.selectedFieldId = null
    this.selectedSectionId = null
    this.propertiesEmptyTarget.classList.remove('d-none')
    this.propertiesFormTarget.classList.add('d-none')
    this.propertiesSectionFormTarget.classList.add('d-none')
    this.propertiesGroupFormTarget.classList.add('d-none')
    this.propertiesPageBreakFormTarget.classList.add('d-none')
    this.propertiesHeadTarget.textContent = trans(PROPERTIES_PANE_HEAD, {}, 'form-types')
  }

  async moveSectionUp (event: Event): Promise<void> {
    event.stopPropagation()

    const section = this.sectionFrom(event)
    const previous = section?.previousElementSibling

    if (!section || !previous) {
      return
    }

    await this.moveSection(() => previous.before(section))
  }

  async moveSectionDown (event: Event): Promise<void> {
    event.stopPropagation()

    const section = this.sectionFrom(event)
    const next = section?.nextElementSibling

    if (!section || !next) {
      return
    }

    await this.moveSection(() => next.after(section))
  }

  private async moveSection (move: () => void): Promise<void> {
    const before = this.sections().map(section => section.dataset.sectionId ?? '')

    this.clearError()
    move()

    const after = this.sections().map(section => section.dataset.sectionId ?? '')

    try {
      await fetchWithHandlingLoginRedirect(this.reorderSectionsUrlValue, {
        method: 'POST',
        headers: { ...XHR_HEADERS, 'Content-Type': 'application/json' },
        body: JSON.stringify({ section_ids: after })
      })

      this.announceChange()
    } catch {
      this.applySectionOrder(before)
      this.showError(trans(MOVE_SECTION_FAILED, {}, 'form-types'))
    }
  }

  private applySectionOrder (sectionIds: string[]): void {
    sectionIds.forEach(sectionId => {
      const section = this.sections().find(candidate => candidate.dataset.sectionId === sectionId)

      if (section) {
        this.sectionListTarget.appendChild(section)
      }
    })
  }

  private placeSectionRowAt (row: HTMLElement, index: number): void {
    this.sectionListTarget.appendChild(row)

    const rowId = row.dataset.sectionId ?? ''
    const ids = this.sections().map(candidate => candidate.dataset.sectionId ?? '').filter(id => id !== rowId)

    ids.splice(index, 0, rowId)

    this.applySectionOrder(ids)
  }

  private removeRowsByIds (fieldIds: string[]): void {
    const ids = new Set(fieldIds)

    this.rows().forEach(row => {
      if (ids.has(row.dataset.fieldId ?? '')) {
        row.remove()
      }
    })
  }

  /**
   * Recreates a deleted section and all of its fields in a single request, rather than one add/update
   * round-trip per field, so undoing a large section stays fast.
   */
  private async restoreSectionWithFields (sectionId: string, name: string, index: number, fields: MergedFieldRecord[], conditionParentFieldId: string, conditionAnswer: string, dependents: DependentCondition[]): Promise<MergedSection | null> {
    try {
      const response = await fetchWithHandlingLoginRedirect(this.restoreSectionUrlValue, {
        method: 'POST',
        headers: { ...XHR_HEADERS, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: sectionId,
          name,
          index,
          conditionParentFieldId,
          conditionAnswer,
          fields: fields.map(record => ({
            id: record.fieldId,
            type: record.snapshot.type,
            name: record.snapshot.name,
            description: record.snapshot.description,
            kind: record.snapshot.kind,
            display: record.snapshot.display,
            min: record.snapshot.min,
            max: record.snapshot.max,
            maxLength: record.snapshot.maxLength,
            isDateOnly: record.snapshot.isDateOnly,
            allowNotApplicable: record.snapshot.allowNotApplicable,
            allowMultipleMedia: record.snapshot.allowMultipleMedia,
            allowAnnotation: record.snapshot.allowAnnotation,
            allowMultipleAnswers: record.snapshot.allowMultipleAnswers,
            options: record.snapshot.options,
            rows: record.snapshot.rows,
            isRequired: record.snapshot.isRequired,
            isLeadField: record.snapshot.isLeadField,
            conditionParentFieldId: record.snapshot.conditionParentFieldId,
            conditionAnswer: record.snapshot.conditionAnswer
          })),
          dependents: dependents.map(dependent => this.dependentBody(dependent))
        })
      })

      const body = await response.json() as RestoreSectionResponse

      return body.result.section
    } catch {
      return null
    }
  }

  /**
   * The inverse of mergeSection(): recreates a section and moves the given, already-existing fields
   * into it by id, in one call, so undoing a merge does not need one delete per field either.
   */
  private async unmergeSection (sectionId: string, name: string, index: number, fieldIds: string[]): Promise<MergedSection | null> {
    try {
      const response = await fetchWithHandlingLoginRedirect(this.unmergeSectionUrlValue, {
        method: 'POST',
        headers: { ...XHR_HEADERS, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: sectionId, name, index, fieldIds })
      })

      const body = await response.json() as RestoreSectionResponse

      return body.result.section
    } catch {
      return null
    }
  }

  async moveUp (event: Event): Promise<void> {
    const row = this.rowFrom(event)

    if (!row) {
      return
    }

    if (this.isChildRow(row)) {
      const group = this.groupOf(row)
      const siblings = group ? this.childRows(group) : []
      const index = siblings.indexOf(row)
      const target = index > 0 ? siblings[index - 1] : null

      if (!group || !target) {
        return
      }

      await this.reorderChildren(group, () => row.parentElement?.insertBefore(row, target), event)

      return
    }

    if (this.isGluedRow(row)) {
      await this.moveAmongSiblings(row, -1, event)

      return
    }

    const block = this.blockOf(row)
    const previous = block[0].previousElementSibling
    const section = this.sectionOf(row)

    if (!(previous instanceof HTMLElement) || !section) {
      return
    }

    const previousBlockStart = this.blockStartOf(previous)

    await this.reorder(section, () => {
      block.forEach(member => previousBlockStart.before(member))
    }, event)
  }

  async moveDown (event: Event): Promise<void> {
    const row = this.rowFrom(event)

    if (!row) {
      return
    }

    if (this.isChildRow(row)) {
      const group = this.groupOf(row)
      const siblings = group ? this.childRows(group) : []
      const index = siblings.indexOf(row)
      const target = index !== -1 && index < siblings.length - 1 ? siblings[index + 1] : null

      if (!group || !target) {
        return
      }

      await this.reorderChildren(group, () => row.parentElement?.insertBefore(target, row), event)

      return
    }

    if (this.isGluedRow(row)) {
      await this.moveAmongSiblings(row, 1, event)

      return
    }

    const block = this.blockOf(row)
    const next = block[block.length - 1].nextElementSibling
    const section = this.sectionOf(row)

    if (!(next instanceof HTMLElement) || !section) {
      return
    }

    const nextBlockEnd = this.blockEndOf(next)

    await this.reorder(section, () => {
      let anchor = nextBlockEnd

      block.forEach(member => {
        anchor.after(member)
        anchor = member
      })
    }, event)
  }

  /**
   * Drag start lives on the grip specifically, via Pointer Events rather than HTML5 drag-and-drop:
   * native drag-and-drop never fires on touch at all, which left the whole reordering feature
   * unusable on a phone or tablet. Pointer Events give mouse and touch one shared code path instead
   * of two implementations to keep in sync. Nothing is "dragging" yet — that only becomes true once
   * the pointer clears DRAG_ACTIVATION_THRESHOLD_PX, so an ordinary tap on the grip stays a no-op.
   */
  gripPointerDown (event: PointerEvent): void {
    const row = (event.currentTarget as HTMLElement).closest<HTMLElement>(QUESTION_SELECTOR)

    if (!row) {
      return
    }

    this.dragged = row
    this.dragStartPos = { x: event.clientX, y: event.clientY }
    this.dragActive = false
  }

  /**
   * Sections reorder the same way questions do — Pointer Events on the grip — kept as separate state
   * from the question drag so the two can never be confused mid-gesture.
   */
  sectionGripPointerDown (event: PointerEvent): void {
    const section = (event.currentTarget as HTMLElement).closest<HTMLElement>(SECTION_SELECTOR)

    if (!section) {
      return
    }

    this.draggedSection = section
    this.sectionDragStartPos = { x: event.clientX, y: event.clientY }
    this.sectionDragActive = false
  }

  handlePointerMove (event: PointerEvent): void {
    if (this.draggedSection) {
      this.handleSectionPointerMove(event)

      return
    }

    if (!this.dragged || !this.dragStartPos) {
      return
    }

    if (!this.dragActive) {
      const moved = Math.abs(event.clientX - this.dragStartPos.x) + Math.abs(event.clientY - this.dragStartPos.y)

      if (moved < DRAG_ACTIVATION_THRESHOLD_PX) {
        return
      }

      this.dragActive = true
      this.dragged.classList.add(DRAGGING_CLASS)
      this.dragGhost = this.buildDragGhost(this.dragged)
      document.body.appendChild(this.dragGhost)
    }

    event.preventDefault()

    if (this.dragGhost) {
      this.dragGhost.style.left = `${event.clientX + 14}px`
      this.dragGhost.style.top = `${event.clientY + 14}px`
    }

    // keep long forms draggable: creep the page when the pointer nears the viewport edges
    if (event.clientY < EDGE_SCROLL_ZONE_PX) {
      window.scrollBy(0, -EDGE_SCROLL_STEP_PX)
    } else if (event.clientY > window.innerHeight - EDGE_SCROLL_ZONE_PX) {
      window.scrollBy(0, EDGE_SCROLL_STEP_PX)
    }

    this.updateDropTarget(event.clientX, event.clientY)
  }

  private handleSectionPointerMove (event: PointerEvent): void {
    if (!this.sectionDragStartPos || !this.draggedSection) {
      return
    }

    if (!this.sectionDragActive) {
      const moved = Math.abs(event.clientX - this.sectionDragStartPos.x) + Math.abs(event.clientY - this.sectionDragStartPos.y)

      if (moved < DRAG_ACTIVATION_THRESHOLD_PX) {
        return
      }

      this.sectionDragActive = true
      this.draggedSection.classList.add(SECTION_DRAGGING_CLASS)
      this.sectionDragGhost = this.buildSectionDragGhost(this.draggedSection)
      document.body.appendChild(this.sectionDragGhost)
    }

    event.preventDefault()

    if (this.sectionDragGhost) {
      this.sectionDragGhost.style.left = `${event.clientX + 14}px`
      this.sectionDragGhost.style.top = `${event.clientY + 14}px`
    }

    if (event.clientY < EDGE_SCROLL_ZONE_PX) {
      window.scrollBy(0, -EDGE_SCROLL_STEP_PX)
    } else if (event.clientY > window.innerHeight - EDGE_SCROLL_ZONE_PX) {
      window.scrollBy(0, EDGE_SCROLL_STEP_PX)
    }

    this.updateSectionDropTarget(event.clientX, event.clientY)
  }

  async handlePointerUp (): Promise<void> {
    const sectionDragEnded = this.endSectionDrag(true)
    const questionDragEnded = this.endDrag(true)

    await sectionDragEnded
    await questionDragEnded
  }

  async handlePointerCancel (): Promise<void> {
    const sectionDragEnded = this.endSectionDrag(false)
    const questionDragEnded = this.endDrag(false)

    await sectionDragEnded
    await questionDragEnded
  }

  private buildDragGhost (row: HTMLElement): HTMLElement {
    const ghost = document.createElement('div')

    ghost.className = 'ft-drag-ghost'
    ghost.textContent = row.querySelector('.ft-question__name')?.textContent ?? ''

    return ghost
  }

  private buildSectionDragGhost (section: HTMLElement): HTMLElement {
    const ghost = document.createElement('div')

    ghost.className = 'ft-drag-ghost'
    ghost.textContent = section.querySelector('.ft-section__name')?.textContent ?? ''

    return ghost
  }

  private updateSectionDropTarget (x: number, y: number): void {
    this.clearSectionDropTargets()
    this.sectionDropTarget = null

    if (!this.draggedSection) {
      return
    }

    const el = document.elementFromPoint(x, y)

    if (!(el instanceof Element)) {
      return
    }

    const section = el.closest<HTMLElement>(SECTION_SELECTOR)

    if (!section || section === this.draggedSection) {
      return
    }

    const box = section.getBoundingClientRect()
    const isAfter = y > box.top + box.height / 2

    section.classList.add(SECTION_DROP_TARGET_CLASS)
    this.sectionDropTarget = { section, isAfter }
  }

  private clearSectionDropTargets (): void {
    this.sections().forEach(section => section.classList.remove(SECTION_DROP_TARGET_CLASS))
  }

  private async endSectionDrag (commit: boolean): Promise<void> {
    const dragged = this.draggedSection
    const target = this.sectionDropTarget
    const wasActive = this.sectionDragActive

    this.sectionDragGhost?.remove()
    this.sectionDragGhost = null
    this.draggedSection?.classList.remove(SECTION_DRAGGING_CLASS)
    this.draggedSection = null
    this.sectionDragStartPos = null
    this.sectionDragActive = false
    this.sectionDropTarget = null
    this.clearSectionDropTargets()

    if (wasActive) {
      this.dragEndedAt = Date.now()
    }

    if (!commit || !wasActive || !dragged || !target || target.section === dragged) {
      return
    }

    await this.moveSection(() => {
      if (target.isAfter) {
        target.section.after(dragged)
      } else {
        target.section.before(dragged)
      }
    })
  }

  private updateDropTarget (x: number, y: number): void {
    this.clearDropTargets()
    this.dropTarget = null

    if (!this.dragged) {
      return
    }

    const el = document.elementFromPoint(x, y)

    if (!(el instanceof Element)) {
      return
    }

    const addRow = el.closest<HTMLElement>('.ft-addrow')

    if (addRow) {
      // a repeating group has its own "add a row question" affordance, styled the same way but not a
      // valid cross-section drop zone — only a section's own add-row counts here. Stop rather than
      // fall through to row hit-testing: a group's add-row sits inside the group's own question card,
      // which would otherwise resolve as "drop next to this group".
      if (!addRow.closest(GROUP_BODY_SELECTOR) && !this.isGluedRow(this.dragged)) {
        addRow.classList.add(ADD_ROW_DROP_TARGET_CLASS)
        this.dropTarget = { kind: 'addRow', addRow }
      }

      return
    }

    const row = el.closest<HTMLElement>(QUESTION_SELECTOR)

    if (!row || this.isChildRow(row) || this.blockOf(this.dragged).includes(row)) {
      return
    }

    if (this.isGluedRow(this.dragged) ? !this.isSiblingOf(row, this.dragged) : this.isGluedRow(row)) {
      return
    }

    const box = row.getBoundingClientRect()
    const isAfter = y > box.top + box.height / 2

    this.markDropTarget(row)
    this.dropTarget = { kind: 'row', row, isAfter }
  }

  private async endDrag (commit: boolean): Promise<void> {
    const dragged = this.dragged
    const target = this.dropTarget
    const wasActive = this.dragActive

    this.dragGhost?.remove()
    this.dragGhost = null
    this.dragged?.classList.remove(DRAGGING_CLASS)
    this.dragged = null
    this.dragStartPos = null
    this.dragActive = false
    this.dropTarget = null
    this.clearDropTargets()

    if (wasActive) {
      this.dragEndedAt = Date.now()
    }

    if (!commit || !wasActive || !dragged || !target) {
      return
    }

    if (target.kind === 'addRow') {
      await this.dropOnAddRow(dragged, target.addRow)
    } else {
      await this.dropOnRow(dragged, target.row, target.isAfter)
    }
  }

  private async dropOnRow (dragged: HTMLElement, row: HTMLElement, isAfter: boolean): Promise<void> {
    const draggedBlock = this.blockOf(dragged)

    if (row === dragged || draggedBlock.includes(row)) {
      return
    }

    if (this.isGluedRow(dragged)) {
      await this.placeAmongSiblings(dragged, row, isAfter)

      return
    }

    const targetSection = this.sectionOf(row)
    const sourceSection = this.sectionOf(dragged)

    if (!targetSection || !sourceSection) {
      return
    }

    if (targetSection === sourceSection) {
      await this.reorder(targetSection, () => {
        if (isAfter) {
          const targetBlockEnd = this.blockEndOf(row)
          let anchor = targetBlockEnd

          draggedBlock.forEach(member => {
            anchor.after(member)
            anchor = member
          })
        } else {
          draggedBlock.forEach(member => row.before(member))
        }
      })

      return
    }

    const targetRows = this.rowsInSection(targetSection)
    const index = isAfter ? targetRows.indexOf(this.blockEndOf(row)) + 1 : targetRows.indexOf(row)

    await this.moveAcrossSections(draggedBlock, targetSection, index)
  }

  private async moveAmongSiblings (row: HTMLElement, direction: -1 | 1, event: Event): Promise<void> {
    const siblings = this.siblingRowsOf(row)
    const target = siblings[siblings.indexOf(row) + direction]

    if (!target) {
      return
    }

    await this.placeAmongSiblings(row, target, direction > 0, event)
  }

  private async placeAmongSiblings (row: HTMLElement, target: HTMLElement, isAfter: boolean, focusEvent?: Event): Promise<void> {
    const section = this.sectionOf(row)

    if (!section || !this.isSiblingOf(target, row)) {
      return
    }

    const block = this.blockOf(row)

    await this.reorder(section, () => {
      if (!isAfter) {
        block.forEach(member => target.before(member))

        return
      }

      const targetBlock = this.blockOf(target)
      let anchor = targetBlock[targetBlock.length - 1]

      block.forEach(member => {
        anchor.after(member)
        anchor = member
      })
    }, focusEvent)
  }

  private siblingRowsOf (row: HTMLElement): HTMLElement[] {
    return this.rows().filter(candidate => candidate === row || this.isSiblingOf(candidate, row))
  }

  private isSiblingOf (candidate: HTMLElement, row: HTMLElement): boolean {
    const parentId = row.dataset.fieldConditionParentId ?? ''

    return candidate !== row &&
      !this.isChildRow(candidate) &&
      parentId !== '' &&
      candidate.dataset.fieldConditionParentId === parentId
  }

  private async dropOnAddRow (dragged: HTMLElement, addRow: HTMLElement): Promise<void> {
    const targetSection = this.sectionOf(addRow)
    const sourceSection = this.sectionOf(dragged)

    if (!targetSection || !sourceSection) {
      return
    }

    const draggedBlock = this.blockOf(dragged)

    if (targetSection === sourceSection) {
      await this.reorder(targetSection, () => {
        const list = targetSection.querySelector('.ft-questions')

        draggedBlock.forEach(member => list?.appendChild(member))
      })

      return
    }

    await this.moveAcrossSections(draggedBlock, targetSection, this.rowsInSection(targetSection).length)
  }

  /**
   * Below the three-pane width, the palette and properties panes become bottom sheets summoned on
   * demand rather than always-visible stacked blocks (Google Forms / Jotform's mobile pattern). The
   * toggle is CSS-only (a class applied unconditionally here, scoped to the narrow media query), so
   * this needs no matchMedia bookkeeping and never drifts out of sync across a resize.
   */
  openPaletteSheet (): void {
    this.openSheet(this.paletteSheetTarget)
  }

  private openPropertiesSheet (): void {
    this.openSheet(this.propertiesSheetTarget)
  }

  private openSheet (pane: HTMLElement): void {
    this.paletteSheetTarget.classList.remove(SHEET_OPEN_CLASS)
    this.propertiesSheetTarget.classList.remove(SHEET_OPEN_CLASS)
    pane.classList.add(SHEET_OPEN_CLASS)
    this.sheetBackdropTarget.classList.add(SHEET_OPEN_CLASS)
  }

  closeSheets (): void {
    this.paletteSheetTarget.classList.remove(SHEET_OPEN_CLASS)
    this.propertiesSheetTarget.classList.remove(SHEET_OPEN_CLASS)
    this.sheetBackdropTarget.classList.remove(SHEET_OPEN_CLASS)
  }

  closeSheetsOnEscape (event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closeSheets()
    }
  }

  select (event: Event): void {
    event.stopPropagation?.()

    if (Date.now() - this.dragEndedAt < CLICK_SUPPRESSION_WINDOW_MS) {
      return
    }

    this.selectRow(event.currentTarget as HTMLElement)
  }

  private selectRow (row: HTMLElement): void {
    this.openPropertiesSheet()
    this.allRows().forEach(candidate => candidate.classList.remove(SELECTED_CLASS))
    row.classList.add(SELECTED_CLASS)

    this.selectedFieldId = row.dataset.fieldId ?? null

    this.sections().forEach(candidate => candidate.querySelector('.ft-section__label')?.classList.remove(SECTION_SELECTED_CLASS))
    this.selectedSectionId = null

    const name = row.querySelector('.ft-question__name')?.textContent ?? ''

    if (row.dataset.fieldType === GROUP_TYPE) {
      this.propertiesEmptyTarget.classList.add('d-none')
      this.propertiesSectionFormTarget.classList.add('d-none')
      this.propertiesFormTarget.classList.add('d-none')
      this.propertiesPageBreakFormTarget.classList.add('d-none')
      this.propertiesGroupFormTarget.classList.remove('d-none')
      this.propertiesHeadTarget.textContent = trans(PROPERTIES_PANE_HEAD_GROUP, { '%name%': name }, 'form-types')
      this.propertiesGroupNameTarget.value = name

      this.populateConditionParentOptions(
        row,
        this.propertiesGroupConditionParentTarget,
        this.propertiesGroupConditionAnswerTarget,
        this.propertiesGroupConditionEmptyNoteTarget
      )

      return
    }

    if (row.dataset.fieldType === PAGE_BREAK_TYPE) {
      this.propertiesEmptyTarget.classList.add('d-none')
      this.propertiesSectionFormTarget.classList.add('d-none')
      this.propertiesGroupFormTarget.classList.add('d-none')
      this.propertiesFormTarget.classList.add('d-none')
      this.propertiesPageBreakFormTarget.classList.remove('d-none')
      this.propertiesHeadTarget.textContent = trans(PROPERTIES_PANE_HEAD_PAGE_BREAK, {}, 'form-types')

      return
    }

    this.propertiesEmptyTarget.classList.add('d-none')
    this.propertiesSectionFormTarget.classList.add('d-none')
    this.propertiesGroupFormTarget.classList.add('d-none')
    this.propertiesPageBreakFormTarget.classList.add('d-none')
    this.propertiesFormTarget.classList.remove('d-none')

    const type = row.dataset.fieldType ?? ''

    this.propertiesHeadTarget.textContent = this.questionPaneHead(type, name)
    this.propertiesLabelTarget.value = name
    this.setDescriptionValue(row.dataset.fieldDescription ?? '')
    const display = row.dataset.fieldDisplay ?? ''
    const isChild = this.isChildRow(row)

    this.propertiesTypeTarget.value = type
    this.applyTypeVisibility(type, display, isChild)
    this.propertiesKindTarget.value = row.dataset.fieldKind ?? ''
    this.propertiesMinTarget.value = row.dataset.fieldMin ?? ''
    this.propertiesMaxTarget.value = row.dataset.fieldMax ?? ''
    this.propertiesMaxLengthTarget.value = row.dataset.fieldMaxLength ?? ''
    this.propertiesDateOnlyTarget.value = row.dataset.fieldIsDateOnly === '1' ? '1' : ''
    this.propertiesAllowNotApplicableTarget.checked = row.dataset.fieldAllowNotApplicable === '1'
    this.propertiesAllowMultipleMediaTarget.checked = row.dataset.fieldAllowMultipleMedia === '1'
    this.propertiesAllowAnnotationTarget.checked = row.dataset.fieldAllowAnnotation === '1'
    this.propertiesAllowMultipleAnswersTarget.checked = row.dataset.fieldAllowMultipleAnswers === '1'
    this.propertiesDisplayTarget.value = display
    this.propertiesOptionsLabelTarget.textContent = trans(display === GRID_DISPLAY ? OPTIONS_AS_COLUMNS : OPTIONS, {}, 'form-types')
    this.renderOptions(this.parseOptions(row.dataset.fieldOptions))
    this.renderRows(this.parseRows(row.dataset.fieldRows))
    this.propertiesRequiredTarget.checked = row.dataset.fieldIsOptional !== '1'

    const canBeLeadField = type === SHORT_TEXT_TYPE || type === LONG_TEXT_TYPE

    this.propertiesLeadFieldTarget.checked = !isChild && canBeLeadField && row.dataset.fieldIsLeadField === '1'

    this.propertiesConditionGroupTarget.classList.toggle('d-none', isChild)

    if (!isChild) {
      this.populateConditionParentOptions(
        row,
        this.propertiesConditionParentTarget,
        this.propertiesConditionAnswerTarget,
        this.propertiesConditionEmptyNoteTarget
      )
    }
  }

  conditionParentChanged (event: Event): void {
    const select = event.currentTarget as HTMLSelectElement
    const answerTarget = select === this.propertiesGroupConditionParentTarget
      ? this.propertiesGroupConditionAnswerTarget
      : select === this.propertiesSectionConditionParentTarget
        ? this.propertiesSectionConditionAnswerTarget
        : this.propertiesConditionAnswerTarget

    this.refreshConditionAnswerOptions(answerTarget, select.value, '')
  }

  private populateConditionParentOptions (
    row: HTMLElement,
    parentTarget: HTMLSelectElement,
    answerTarget: HTMLSelectElement,
    emptyNoteTarget: HTMLElement
  ): void {
    const currentParentId = row.dataset.fieldConditionParentId ?? ''
    const { eligible, capped } = this.conditionParentCandidates(row)

    parentTarget.innerHTML = ''

    const always = document.createElement('option')

    always.value = ''
    always.textContent = trans(ALWAYS_SHOWN, {}, 'form-types')
    parentTarget.appendChild(always)

    eligible.forEach(candidate => {
      const option = document.createElement('option')
      const candidateId = candidate.dataset.fieldId ?? ''

      option.value = candidateId
      option.textContent = candidate.querySelector('.ft-question__name')?.textContent ?? ''

      if (candidateId === currentParentId) {
        option.selected = true
      }

      parentTarget.appendChild(option)
    })

    const hasCurrentParent = eligible.some(candidate => candidate.dataset.fieldId === currentParentId)

    parentTarget.disabled = eligible.length === 0
    emptyNoteTarget.classList.toggle('d-none', eligible.length > 0)
    emptyNoteTarget.textContent = trans(capped ? CONDITION_CAPPED : CONDITION_NO_ELIGIBLE_PARENT, {}, 'form-types')

    const effectiveParentId = hasCurrentParent ? currentParentId : ''

    parentTarget.value = effectiveParentId

    this.refreshConditionAnswerOptions(answerTarget, effectiveParentId, row.dataset.fieldConditionAnswer ?? '')
  }

  private refreshConditionAnswerOptions (answerTarget: HTMLSelectElement, parentId: string, selectedAnswer: string): void {
    answerTarget.innerHTML = ''

    if (!parentId) {
      answerTarget.classList.add('d-none')

      return
    }

    const parentRow = this.findRow(parentId)
    const answers = parentRow ? this.answersFor(parentRow) : []

    answers.forEach(answer => {
      const option = document.createElement('option')

      option.value = answer.value
      option.textContent = answer.label

      if (answer.value === selectedAnswer) {
        option.selected = true
      }

      answerTarget.appendChild(option)
    })

    answerTarget.classList.toggle('d-none', answers.length === 0)

    if (!answers.some(answer => answer.value === selectedAnswer)) {
      answerTarget.value = answers[0]?.value ?? ''
    }
  }

  private answersFor (row: HTMLElement): Array<{ value: string, label: string }> {
    if (row.dataset.fieldType === YES_NO_TYPE) {
      const answers = [
        { value: 'yes', label: trans(YES, {}, 'actions') },
        { value: 'no', label: trans(NO, {}, 'actions') }
      ]

      if (row.dataset.fieldAllowNotApplicable === '1') {
        answers.push({ value: 'na', label: trans(NOT_APPLICABLE, {}, 'forms') })
      }

      return answers
    }

    return this.parseOptions(row.dataset.fieldOptions).map(value => ({ value, label: value }))
  }

  private conditionParentCandidates (row: HTMLElement): { eligible: HTMLElement[], capped: boolean } {
    let capped = false

    const eligible = this.rows().filter(candidate => {
      if (candidate === row) {
        return false
      }

      const candidateType = candidate.dataset.fieldType ?? ''
      const isEligibleType = candidateType === YES_NO_TYPE ||
        (candidateType === CHOICE_TYPE && candidate.dataset.fieldDisplay !== GRID_DISPLAY && candidate.dataset.fieldAllowMultipleAnswers !== '1')

      if (!isEligibleType) {
        return false
      }

      if (this.conditionChainContains(candidate, row)) {
        return false
      }

      const depth = this.conditionDepthOf(candidate) + 1 + this.conditionHeightOf(row)

      if (depth > MAX_CONDITION_DEPTH) {
        capped = true

        return false
      }

      return true
    })

    return { eligible, capped }
  }

  private populateSectionConditionParentOptions (
    sectionElement: HTMLElement,
    parentTarget: HTMLSelectElement,
    answerTarget: HTMLSelectElement,
    emptyNoteTarget: HTMLElement,
    noteTarget: HTMLElement
  ): void {
    const currentParentId = sectionElement.dataset.sectionConditionParentId ?? ''
    const eligible = this.sectionConditionParentCandidates(sectionElement)

    parentTarget.innerHTML = ''

    const always = document.createElement('option')

    always.value = ''
    always.textContent = trans(ALWAYS_SHOWN, {}, 'form-types')
    parentTarget.appendChild(always)

    eligible.forEach(candidate => {
      const option = document.createElement('option')
      const candidateId = candidate.dataset.fieldId ?? ''

      option.value = candidateId
      option.textContent = candidate.querySelector('.ft-question__name')?.textContent ?? ''

      if (candidateId === currentParentId) {
        option.selected = true
      }

      parentTarget.appendChild(option)
    })

    const hasCurrentParent = eligible.some(candidate => candidate.dataset.fieldId === currentParentId)

    parentTarget.disabled = eligible.length === 0
    emptyNoteTarget.classList.toggle('d-none', eligible.length > 0)
    emptyNoteTarget.textContent = trans(SECTION_CONDITION_NO_ELIGIBLE_PARENT, {}, 'form-types')

    const effectiveParentId = hasCurrentParent ? currentParentId : ''

    parentTarget.value = effectiveParentId
    noteTarget.classList.toggle('d-none', !effectiveParentId)

    this.refreshConditionAnswerOptions(answerTarget, effectiveParentId, sectionElement.dataset.sectionConditionAnswer ?? '')
  }

  private sectionConditionParentCandidates (sectionElement: HTMLElement): HTMLElement[] {
    const sections = this.sections()
    const sectionIndex = sections.indexOf(sectionElement)

    return this.rows().filter(candidate => {
      if (this.isGluedRow(candidate)) {
        return false
      }

      const candidateType = candidate.dataset.fieldType ?? ''
      const isEligibleType = candidateType === YES_NO_TYPE ||
        (candidateType === CHOICE_TYPE && candidate.dataset.fieldDisplay !== GRID_DISPLAY && candidate.dataset.fieldAllowMultipleAnswers !== '1')

      if (!isEligibleType) {
        return false
      }

      const candidateSection = this.sectionOf(candidate)
      const candidateSectionIndex = candidateSection ? sections.indexOf(candidateSection) : -1

      return candidateSectionIndex !== -1 && candidateSectionIndex < sectionIndex
    })
  }

  private conditionDepthOf (row: HTMLElement): number {
    let depth = 0
    let current: HTMLElement | null = row
    let hops = 0

    while (current?.dataset.fieldConditionParentId && hops++ < 10) {
      depth++
      current = this.findRow(current.dataset.fieldConditionParentId)
    }

    return depth
  }

  private refreshConditionDepths (): void {
    this.allRows().forEach(row => {
      const depth = this.isGluedRow(row) ? this.conditionDepthOf(row) : 0

      if (depth > 0) {
        row.dataset.conditionDepth = String(depth)
      } else {
        delete row.dataset.conditionDepth
      }
    })
  }

  private conditionChainContains (row: HTMLElement, target: HTMLElement): boolean {
    const targetId = target.dataset.fieldId ?? ''
    let current: HTMLElement | null = row
    let hops = 0

    while (current?.dataset.fieldConditionParentId && hops++ < 10) {
      if (current.dataset.fieldConditionParentId === targetId) {
        return true
      }

      current = this.findRow(current.dataset.fieldConditionParentId)
    }

    return false
  }

  private conditionHeightOf (row: HTMLElement): number {
    const rowId = row.dataset.fieldId ?? ''
    let height = 0

    this.rows().forEach(candidate => {
      if (candidate.dataset.fieldConditionParentId === rowId) {
        height = Math.max(height, 1 + this.conditionHeightOf(candidate))
      }
    })

    return height
  }

  private updateConditionalTag (row: HTMLElement, hasCondition: boolean): void {
    row.querySelector('.ft-tag--conditional')?.classList.toggle('d-none', !hasCondition)
  }

  /**
   * Applies the server's verdict on a field's condition to the DOM: updates the stored parent/answer,
   * the "Conditional" tag, whether the row is glued (locked in place, undraggable), and — when a NEW
   * parent was just set — moves the row to sit right after that parent's block, mirroring the
   * repositioning the backend already did.
   */
  private applyConditionResult (row: HTMLElement, previousParentId: string, newParentId: string | null | undefined, newAnswer: string | null | undefined): void {
    const resolvedParentId = newParentId ?? ''

    row.dataset.fieldConditionParentId = resolvedParentId
    row.dataset.fieldConditionAnswer = newAnswer ?? ''
    this.updateConditionalTag(row, Boolean(resolvedParentId))
    this.applyGluedState(row, Boolean(resolvedParentId))

    if (resolvedParentId && resolvedParentId !== previousParentId) {
      this.moveRowAfterConditionParent(row, resolvedParentId)
    }
  }

  private applyDependentResults (dependents: DependentConditionResult[]): void {
    dependents.forEach(dependent => {
      if (dependent.kind === 'section') {
        const section = this.sections().find(candidate => candidate.dataset.sectionId === dependent.id)

        if (section) {
          this.applySectionConditionResult(section, dependent.condition_parent_field_id, dependent.condition_answer)
        }

        return
      }

      const row = this.findRow(dependent.id)

      if (row) {
        this.applyConditionResult(row, row.dataset.fieldConditionParentId ?? '', dependent.condition_parent_field_id, dependent.condition_answer)
      }
    })
  }

  private applyGluedState (row: HTMLElement, isGlued: boolean): void {
    row.classList.toggle('ft-question--glued', isGlued)
    row.draggable = false
  }

  private moveRowAfterConditionParent (row: HTMLElement, parentId: string): void {
    const parentRow = this.findRow(parentId)

    if (!parentRow) {
      return
    }

    const block = this.blockOf(row)
    let anchor = this.conditionBlockEndOf(parentRow, block)

    block.forEach(member => {
      anchor.after(member)
      anchor = member
    })
  }

  /**
   * Mirrors the server's cascade-clear: a deleted field can never again be a valid condition parent,
   * so any row still pointing at it in the DOM is now stale and would otherwise show a "Conditional"
   * tag and eligibility state the backend has already cleared.
   */
  private clearDependentConditionsInDom (deletedFieldId: string): void {
    this.allRows().forEach(candidate => {
      if (candidate.dataset.fieldConditionParentId === deletedFieldId) {
        candidate.dataset.fieldConditionParentId = ''
        candidate.dataset.fieldConditionAnswer = ''
        this.updateConditionalTag(candidate, false)
        this.applyGluedState(candidate, false)
      }
    })

    this.sections().forEach(section => {
      if (section.dataset.sectionConditionParentId === deletedFieldId) {
        this.applySectionConditionResult(section, null, null)
      }
    })
  }

  private dependentConditionsOf (fieldId: string): DependentCondition[] {
    const fields = this.allRows()
      .filter(candidate => candidate.dataset.fieldConditionParentId === fieldId)
      .map(candidate => ({ kind: 'field' as const, id: candidate.dataset.fieldId ?? '', parentId: fieldId, answer: candidate.dataset.fieldConditionAnswer ?? '' }))

    const sections = this.sections()
      .filter(section => section.dataset.sectionConditionParentId === fieldId)
      .map(section => ({ kind: 'section' as const, id: section.dataset.sectionId ?? '', parentId: fieldId, answer: section.dataset.sectionConditionAnswer ?? '' }))

    return [...fields, ...sections]
  }

  private sectionDependentsOf (sectionId: string, fieldIds: string[]): DependentCondition[] {
    return fieldIds
      .flatMap(fieldId => this.dependentConditionsOf(fieldId))
      .filter(dependent => dependent.kind === 'section' ? dependent.id !== sectionId : !fieldIds.includes(dependent.id))
  }

  private dependentBody (dependent: DependentCondition): Record<string, string> {
    return { kind: dependent.kind, id: dependent.id, parentFieldId: dependent.parentId, answer: dependent.answer }
  }

  private conditionStateOf (row: HTMLElement): ConditionState {
    const section = this.sectionOf(row)

    return {
      parentId: row.dataset.fieldConditionParentId ?? '',
      answer: row.dataset.fieldConditionAnswer ?? '',
      sectionId: section?.dataset.sectionId ?? '',
      order: section ? this.rowsInSection(section).map(candidate => candidate.dataset.fieldId ?? '') : []
    }
  }

  private recordConditionChange (row: HTMLElement, before: ConditionState): void {
    const after = this.conditionStateOf(row)

    if (after.parentId === before.parentId && after.answer === before.answer) {
      return
    }

    this.record({ kind: 'condition', fieldId: row.dataset.fieldId ?? '', before, after })
  }

  private async applyConditionState (fieldId: string, state: ConditionState): Promise<boolean> {
    const row = this.findRow(fieldId)
    const section = this.sections().find(candidate => candidate.dataset.sectionId === state.sectionId)

    if (!row || !section) {
      return false
    }

    let field: AddedField

    try {
      const response = await fetchWithHandlingLoginRedirect(this.restoreConditionUrlTemplateValue.replace(PLACEHOLDER, fieldId), {
        method: 'POST',
        headers: { ...XHR_HEADERS, 'Content-Type': 'application/json' },
        body: JSON.stringify({ conditionParentFieldId: state.parentId, conditionAnswer: state.answer, sectionId: state.sectionId, fieldIds: state.order })
      })

      field = (await response.json() as AddFieldResponse).result.field
    } catch {
      return false
    }

    const block = this.blockOf(row)

    this.applyConditionResult(row, row.dataset.fieldConditionParentId ?? '', field.condition_parent_field_id, field.condition_answer)

    if (this.sectionOf(row) !== section) {
      this.placeRowsInSection(section, block, 0)
    }

    this.applyOrderToRows(section, state.order)

    if (this.selectedFieldId === fieldId) {
      this.selectRow(row)
    }

    return true
  }

  /**
   * Clicking "Add" while still focused on the previous option's input fires that input's change-triggered
   * save first; awaiting any save already in flight keeps the new row from being appended before that
   * save settles, so a failed save's rollback cannot wipe it out.
   */
  async addOption (): Promise<void> {
    if (this.pendingPropertiesSave) {
      await this.pendingPropertiesSave
    }

    this.appendOptionRow('')?.focus()
  }

  async removeOption (event: Event): Promise<void> {
    const target = event.currentTarget as HTMLElement
    target.closest('.ft-optionrow')?.remove()
    await this.saveProperties()
  }

  displayChanged (event: Event): void {
    const select = event.currentTarget as HTMLSelectElement

    this.propertiesOptionsLabelTarget.textContent = trans(select.value === GRID_DISPLAY ? OPTIONS_AS_COLUMNS : OPTIONS, {}, 'form-types')
    this.propertiesRowsGroupTarget.classList.toggle('d-none', select.value !== GRID_DISPLAY)
    this.propertiesChoiceGroupTarget.classList.toggle('d-none', select.value !== '')

    if (select.value !== '') {
      this.propertiesAllowMultipleAnswersTarget.checked = false
    }
  }

  /**
   * A question can be retyped to any other non-structural type at any time, not just fixed forever at
   * creation. Switching the select immediately previews which controls now apply — the save itself
   * (chained on the same 'change' event) is what actually clears whatever no longer fits.
   */
  typeChanged (event: Event): void {
    const select = event.currentTarget as HTMLSelectElement
    const row = this.selectedFieldId ? this.findRow(this.selectedFieldId) : null
    const isChild = row ? this.isChildRow(row) : false

    this.applyTypeVisibility(select.value, this.propertiesDisplayTarget.value, isChild)
  }

  /**
   * The single source of truth for which properties-pane controls apply to a given type (+ display,
   * since Options/Rows also depend on Choice's display). Shared by selecting a row, previewing a type
   * change before it saves, and both the success and failure paths of saving one — so a group's
   * visibility can never drift out of sync with whichever type is actually showing.
   */
  private applyTypeVisibility (type: string, display: string, isChild: boolean): void {
    const isTextBlock = type === TEXT_BLOCK_TYPE

    this.propertiesDescriptionGroupTarget.classList.toggle('d-none', isTextBlock)
    this.propertiesTextBlockGroupTarget.classList.toggle('d-none', !isTextBlock)
    this.propertiesTypeGroupTarget.classList.toggle('d-none', isTextBlock)
    this.propertiesRequiredGroupTarget.classList.toggle('d-none', isTextBlock)
    this.propertiesKindGroupTarget.classList.toggle('d-none', type !== SHORT_TEXT_TYPE)
    this.propertiesRangeGroupTarget.classList.toggle('d-none', type !== NUMBER_TYPE)

    const isText = type === SHORT_TEXT_TYPE || type === LONG_TEXT_TYPE

    this.propertiesMaxLengthGroupTarget.classList.toggle('d-none', !isText)
    this.propertiesDateOnlyGroupTarget.classList.toggle('d-none', type !== DATE_TIME_TYPE)
    this.propertiesYesNoGroupTarget.classList.toggle('d-none', type !== YES_NO_TYPE)
    this.propertiesPhotoGroupTarget.classList.toggle('d-none', type !== MEDIA_TYPE)

    const isChoice = type === CHOICE_TYPE

    this.propertiesDisplayGroupTarget.classList.toggle('d-none', !isChoice)
    this.propertiesChoiceGroupTarget.classList.toggle('d-none', !isChoice || display !== '')
    this.propertiesOptionsGroupTarget.classList.toggle('d-none', !isChoice)
    this.propertiesRowsGroupTarget.classList.toggle('d-none', !isChoice || display !== GRID_DISPLAY)
    const leadFieldGroupHidden = isChild || !isText

    this.propertiesLeadFieldGroupTarget.classList.toggle('d-none', leadFieldGroupHidden)
    this.propertiesLeadFieldAccessNoteTarget.classList.toggle('d-none', leadFieldGroupHidden || this.accessModeValue !== 'teams')
  }

  async addRow (): Promise<void> {
    if (this.pendingPropertiesSave) {
      await this.pendingPropertiesSave
    }

    this.appendRowRow('')?.focus()
  }

  async removeRow (event: Event): Promise<void> {
    const target = event.currentTarget as HTMLElement
    target.closest('.ft-optionrow')?.remove()
    await this.saveProperties()
  }

  async saveProperties (): Promise<void> {
    const fieldId = this.selectedFieldId
    const payload = this.propertiesPayload()
    const previous = this.pendingPropertiesSave

    const save = (async () => {
      if (previous) {
        await previous
      }

      await this.performSaveProperties(fieldId, payload)
    })()

    this.pendingPropertiesSave = save

    try {
      await save
    } finally {
      if (this.pendingPropertiesSave === save) {
        this.pendingPropertiesSave = null
      }
    }
  }

  private questionPaneHead (type: string, name: string): string {
    return trans(type === TEXT_BLOCK_TYPE ? PROPERTIES_PANE_HEAD_TEXT_BLOCK : PROPERTIES_PANE_HEAD_QUESTION, { '%name%': name }, 'form-types')
  }

  private setDescriptionValue (value: string): void {
    this.propertiesDescriptionTarget.value = value
    this.propertiesTextBlockTarget.value = value
  }

  private descriptionValue (): string {
    const row = this.selectedFieldId ? this.findRow(this.selectedFieldId) : null

    return row?.dataset.fieldType === TEXT_BLOCK_TYPE ? this.propertiesTextBlockTarget.value : this.propertiesDescriptionTarget.value
  }

  private propertiesPayload (): Record<string, unknown> {
    return {
      name: this.propertiesLabelTarget.value,
      description: this.descriptionValue(),
      type: this.propertiesTypeTarget.value,
      kind: this.propertiesKindTarget.value,
      min: this.propertiesMinTarget.value,
      max: this.propertiesMaxTarget.value,
      maxLength: this.propertiesMaxLengthTarget.value,
      isDateOnly: this.propertiesDateOnlyTarget.value === '1',
      allowNotApplicable: this.propertiesAllowNotApplicableTarget.checked,
      allowMultipleMedia: this.propertiesAllowMultipleMediaTarget.checked,
      allowAnnotation: this.propertiesAllowAnnotationTarget.checked,
      allowMultipleAnswers: this.propertiesAllowMultipleAnswersTarget.checked,
      display: this.propertiesDisplayTarget.value,
      options: this.currentOptionValues(),
      rows: this.currentRowValues(),
      isRequired: this.propertiesRequiredTarget.checked,
      isLeadField: this.propertiesLeadFieldTarget.checked,
      conditionParentFieldId: this.propertiesConditionParentTarget.value,
      conditionAnswer: this.propertiesConditionAnswerTarget.value
    }
  }

  private async performSaveProperties (fieldId: string | null, payload: Record<string, unknown>): Promise<void> {
    const row = fieldId ? this.findRow(fieldId) : null

    if (!fieldId || !row) {
      return
    }

    const previousName = row.querySelector('.ft-question__name')?.textContent ?? ''
    const previousDescription = row.dataset.fieldDescription ?? ''
    const previousType = row.dataset.fieldType ?? ''
    const previousKind = row.dataset.fieldKind ?? ''
    const previousMin = row.dataset.fieldMin ?? ''
    const previousMax = row.dataset.fieldMax ?? ''
    const previousMaxLength = row.dataset.fieldMaxLength ?? ''
    const previousIsDateOnly = row.dataset.fieldIsDateOnly === '1' ? '1' : ''
    const previousAllowNotApplicable = row.dataset.fieldAllowNotApplicable === '1'
    const previousAllowMultipleMedia = row.dataset.fieldAllowMultipleMedia === '1'
    const previousAllowAnnotation = row.dataset.fieldAllowAnnotation === '1'
    const previousAllowMultipleAnswers = row.dataset.fieldAllowMultipleAnswers === '1'
    const previousDisplay = row.dataset.fieldDisplay ?? ''
    const previousOptions = this.parseOptions(row.dataset.fieldOptions)
    const previousRows = this.parseRows(row.dataset.fieldRows)
    const previousIsOptional = row.dataset.fieldIsOptional === '1'
    const previousIsLeadField = row.dataset.fieldIsLeadField === '1'
    const previousConditionParentId = row.dataset.fieldConditionParentId ?? ''
    const previousConditionAnswer = row.dataset.fieldConditionAnswer ?? ''
    const conditionBefore = this.conditionStateOf(row)

    this.clearError()

    try {
      const response = await fetchWithHandlingLoginRedirect(
        this.updateUrlTemplateValue.replace(PLACEHOLDER, fieldId),
        {
          method: 'PATCH',
          headers: { ...XHR_HEADERS, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      )

      const body = await response.json() as UpdateFieldResponse
      const field = body.result.field
      const stillSelected = this.selectedFieldId === fieldId

      const nameEl = row.querySelector('.ft-question__name')

      if (nameEl) {
        nameEl.textContent = field.name
      }

      const typeLabelEl = row.querySelector('.ft-question__type')

      if (typeLabelEl && field.type_label) {
        typeLabelEl.textContent = field.type_label
      }

      const iconEl = row.querySelector('.ft-question__icon')

      if (iconEl && field.icon) {
        iconEl.className = `fas ${field.icon} ft-question__icon`
      }

      row.dataset.fieldDescription = field.description
      row.dataset.fieldType = field.type ?? previousType
      row.dataset.fieldKind = field.kind
      row.dataset.fieldMin = field.min === null ? '' : String(field.min)
      row.dataset.fieldMax = field.max === null ? '' : String(field.max)
      row.dataset.fieldMaxLength = (field.max_length === null || field.max_length === undefined) ? '' : String(field.max_length)
      row.dataset.fieldIsDateOnly = field.is_date_only ? '1' : ''
      row.dataset.fieldAllowNotApplicable = field.allow_not_applicable ? '1' : ''
      row.dataset.fieldAllowMultipleMedia = field.allow_multiple_media ? '1' : ''
      row.dataset.fieldAllowAnnotation = field.allow_annotation ? '1' : ''
      row.dataset.fieldAllowMultipleAnswers = field.allow_multiple_answers ? '1' : ''
      row.dataset.fieldDisplay = field.display ?? ''
      row.dataset.fieldOptions = JSON.stringify(field.options)
      row.dataset.fieldRows = JSON.stringify(field.rows ?? [])
      row.dataset.fieldIsOptional = field.is_optional ? '1' : ''
      row.dataset.fieldIsLeadField = field.is_lead_field ? '1' : ''
      this.updateSummary(row, field)
      this.updateChips(row, field.is_optional, field.is_lead_field)

      if (stillSelected) {
        this.propertiesHeadTarget.textContent = this.questionPaneHead(row.dataset.fieldType ?? '', field.name)
        this.propertiesTypeTarget.value = row.dataset.fieldType
        this.propertiesLabelTarget.value = field.name
        this.setDescriptionValue(field.description)
        this.propertiesKindTarget.value = field.kind
        this.propertiesMinTarget.value = row.dataset.fieldMin
        this.propertiesMaxTarget.value = row.dataset.fieldMax
        this.propertiesMaxLengthTarget.value = row.dataset.fieldMaxLength
        this.propertiesDateOnlyTarget.value = row.dataset.fieldIsDateOnly
        this.propertiesAllowNotApplicableTarget.checked = Boolean(field.allow_not_applicable)
        this.propertiesAllowMultipleMediaTarget.checked = field.allow_multiple_media
        this.propertiesAllowAnnotationTarget.checked = field.allow_annotation
        this.propertiesAllowMultipleAnswersTarget.checked = field.allow_multiple_answers
        this.propertiesDisplayTarget.value = field.display ?? ''
        this.propertiesOptionsLabelTarget.textContent = trans(field.display === GRID_DISPLAY ? OPTIONS_AS_COLUMNS : OPTIONS, {}, 'form-types')
        this.applyTypeVisibility(row.dataset.fieldType ?? '', field.display ?? '', this.isChildRow(row))
        this.propertiesRequiredTarget.checked = !field.is_optional
        this.propertiesLeadFieldTarget.checked = field.is_lead_field
      }

      if (field.is_lead_field) {
        this.clearLeadFieldFromOtherRows(fieldId)
      }

      this.applyConditionResult(row, previousConditionParentId, field.condition_parent_field_id, field.condition_answer)
      this.recordConditionChange(row, conditionBefore)
      this.applyDependentResults(body.result.dependents ?? [])
      this.announceChange()
    } catch {
      this.showError(trans(UPDATE_FIELD_FAILED, {}, 'form-types'))

      if (this.selectedFieldId === fieldId) {
        this.propertiesLabelTarget.value = previousName
        this.setDescriptionValue(previousDescription)
        this.propertiesTypeTarget.value = previousType
        this.propertiesKindTarget.value = previousKind
        this.propertiesMinTarget.value = previousMin
        this.propertiesMaxTarget.value = previousMax
        this.propertiesMaxLengthTarget.value = previousMaxLength
        this.propertiesDateOnlyTarget.value = previousIsDateOnly
        this.propertiesAllowNotApplicableTarget.checked = previousAllowNotApplicable
        this.propertiesAllowMultipleMediaTarget.checked = previousAllowMultipleMedia
        this.propertiesAllowAnnotationTarget.checked = previousAllowAnnotation
        this.propertiesAllowMultipleAnswersTarget.checked = previousAllowMultipleAnswers
        this.propertiesDisplayTarget.value = previousDisplay
        this.propertiesOptionsLabelTarget.textContent = trans(previousDisplay === GRID_DISPLAY ? OPTIONS_AS_COLUMNS : OPTIONS, {}, 'form-types')
        this.renderOptions(previousOptions)
        this.applyTypeVisibility(previousType, previousDisplay, this.isChildRow(row))
        this.renderRows(previousRows)
        this.propertiesRequiredTarget.checked = !previousIsOptional
        this.propertiesLeadFieldTarget.checked = previousIsLeadField
        this.populateConditionParentOptions(row, this.propertiesConditionParentTarget, this.propertiesConditionAnswerTarget, this.propertiesConditionEmptyNoteTarget)
      }

      row.dataset.fieldConditionParentId = previousConditionParentId
      row.dataset.fieldConditionAnswer = previousConditionAnswer
    }
  }

  async saveGroupName (): Promise<void> {
    const fieldId = this.selectedFieldId
    const row = fieldId ? this.findRow(fieldId) : null

    if (!fieldId || !row) {
      return
    }

    const previousName = row.querySelector('.ft-question__name')?.textContent ?? ''
    const previousConditionParentId = row.dataset.fieldConditionParentId ?? ''
    const previousConditionAnswer = row.dataset.fieldConditionAnswer ?? ''
    const conditionBefore = this.conditionStateOf(row)

    this.clearError()

    try {
      const response = await fetchWithHandlingLoginRedirect(
        this.updateUrlTemplateValue.replace(PLACEHOLDER, fieldId),
        {
          method: 'PATCH',
          headers: { ...XHR_HEADERS, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: this.propertiesGroupNameTarget.value,
            description: null,
            conditionParentFieldId: this.propertiesGroupConditionParentTarget.value,
            conditionAnswer: this.propertiesGroupConditionAnswerTarget.value
          })
        }
      )

      const body = await response.json() as UpdateFieldResponse
      const field = body.result.field

      const nameEl = row.querySelector('.ft-question__name')

      if (nameEl) {
        nameEl.textContent = field.name
      }

      if (this.selectedFieldId === fieldId) {
        this.propertiesHeadTarget.textContent = trans(PROPERTIES_PANE_HEAD_GROUP, { '%name%': field.name }, 'form-types')
        this.propertiesGroupNameTarget.value = field.name
      }

      this.applyConditionResult(row, previousConditionParentId, field.condition_parent_field_id, field.condition_answer)
      this.recordConditionChange(row, conditionBefore)
      this.announceChange()
    } catch {
      this.showError(trans(UPDATE_FIELD_FAILED, {}, 'form-types'))

      if (this.selectedFieldId === fieldId) {
        this.propertiesGroupNameTarget.value = previousName
        this.populateConditionParentOptions(row, this.propertiesGroupConditionParentTarget, this.propertiesGroupConditionAnswerTarget, this.propertiesGroupConditionEmptyNoteTarget)
      }

      row.dataset.fieldConditionParentId = previousConditionParentId
      row.dataset.fieldConditionAnswer = previousConditionAnswer
    }
  }

  private clearLeadFieldFromOtherRows (keepFieldId: string): void {
    this.rows().forEach(row => {
      if (row.dataset.fieldId !== keepFieldId) {
        row.dataset.fieldIsLeadField = ''
        row.querySelector('.ft-tag--lead')?.classList.add('d-none')
      }
    })
  }

  private updateChips (row: HTMLElement, isOptional: boolean, isLeadField: boolean): void {
    row.querySelector('.ft-tag--req')?.classList.toggle('d-none', isOptional)
    row.querySelector('.ft-tag--lead')?.classList.toggle('d-none', !isLeadField)
  }

  private summaryBits (field: SummaryField): string[] {
    const bits: string[] = []

    if (field.type === SHORT_TEXT_TYPE) {
      if (field.kind) {
        bits.push(trans(FIELD_KIND_LABELS[field.kind] ?? FIELD_KIND_NAME, {}, 'form-types'))
      }

      if (field.max_length) {
        bits.push(trans(FIELD_SUMMARY_MAX_LENGTH, { '%count%': field.max_length }, 'form-types'))
      }
    } else if (field.type === LONG_TEXT_TYPE) {
      if (field.max_length) {
        bits.push(trans(FIELD_SUMMARY_MAX_LENGTH, { '%count%': field.max_length }, 'form-types'))
      }
    } else if (field.type === CHOICE_TYPE) {
      if (field.display === GRID_DISPLAY) {
        const rowCount = field.rows?.length ?? 0
        bits.push(trans(rowCount === 1 ? FIELD_SUMMARY_ROW_ONE : FIELD_SUMMARY_ROW_MANY, { '%count%': rowCount }, 'form-types'))
      }

      const optionCount = field.options?.length ?? 0
      bits.push(trans(optionCount === 1 ? FIELD_SUMMARY_OPTION_ONE : FIELD_SUMMARY_OPTION_MANY, { '%count%': optionCount }, 'form-types'))

      if (field.allow_multiple_answers) {
        bits.push(trans(FIELD_SUMMARY_MULTIPLE_ANSWERS, {}, 'form-types'))
      }
    } else if (field.type === YES_NO_TYPE) {
      if (field.allow_not_applicable) {
        bits.push(trans(FIELD_SUMMARY_NOT_APPLICABLE, {}, 'form-types'))
      }
    } else if (field.type === NUMBER_TYPE) {
      if ((field.min !== null && field.min !== undefined) || (field.max !== null && field.max !== undefined)) {
        const any = trans(FIELD_SUMMARY_RANGE_ANY, {}, 'form-types')

        bits.push(trans(FIELD_SUMMARY_RANGE, {
          '%min%': field.min === null || field.min === undefined ? any : String(field.min),
          '%max%': field.max === null || field.max === undefined ? any : String(field.max)
        }, 'form-types'))
      }
    } else if (field.type === DATE_TIME_TYPE) {
      if (field.is_date_only) {
        bits.push(trans(DATE_ONLY, {}, 'form-types'))
      }
    } else if (field.type === MEDIA_TYPE) {
      if (field.allow_multiple_media) {
        bits.push(trans(FIELD_SUMMARY_MULTIPLE_MEDIA, {}, 'form-types'))
      }

      if (field.allow_annotation) {
        bits.push(trans(FIELD_SUMMARY_ANNOTATION, {}, 'form-types'))
      }
    }

    if (field.description) {
      bits.push(field.description)
    }

    return bits
  }

  private updateSummary (row: HTMLElement, field: SummaryField): void {
    const descriptionEl = row.querySelector('.ft-question__description')

    if (!descriptionEl) {
      return
    }

    const summary = this.summaryBits(field).join(' · ')

    descriptionEl.textContent = summary
    descriptionEl.classList.toggle('d-none', summary === '')
  }

  async duplicateField (): Promise<void> {
    const fieldId = this.selectedFieldId

    if (!fieldId) {
      return
    }

    this.clearError()

    const newFieldId = await this.duplicateFrom(fieldId)

    if (!newFieldId) {
      this.showError(trans(DUPLICATE_FIELD_FAILED, {}, 'form-types'))

      return
    }

    const copy = this.findRow(newFieldId)

    if (copy) {
      this.selectRow(copy)
    }

    this.record({ kind: 'duplicate', sourceFieldId: fieldId, fieldId: newFieldId })
  }

  private async duplicateFrom (sourceFieldId: string, id?: string): Promise<string | null> {
    const sourceRow = this.findRow(sourceFieldId)

    if (!sourceRow) {
      return null
    }

    try {
      const response = await fetchWithHandlingLoginRedirect(
        this.duplicateUrlTemplateValue.replace(PLACEHOLDER, sourceFieldId),
        { method: 'POST', headers: { ...XHR_HEADERS, 'Content-Type': 'application/json' }, body: JSON.stringify({ id }) }
      )

      const body = await response.json() as AddFieldResponse
      const field = body.result.field
      const isChild = this.isChildRow(sourceRow)
      const block = this.blockOf(sourceRow)
      const anchorRow = isChild ? sourceRow : block[block.length - 1]

      return this.insertRowAfter(anchorRow, field, isChild) ? field.id : null
    } catch {
      return null
    }
  }

  private snapshotOf (row: HTMLElement): FieldSnapshot {
    return {
      type: row.dataset.fieldType ?? '',
      name: row.querySelector('.ft-question__name')?.textContent ?? '',
      description: row.dataset.fieldDescription ?? '',
      kind: row.dataset.fieldKind ?? '',
      display: row.dataset.fieldDisplay ?? '',
      min: row.dataset.fieldMin ?? '',
      max: row.dataset.fieldMax ?? '',
      maxLength: row.dataset.fieldMaxLength ?? '',
      isDateOnly: row.dataset.fieldIsDateOnly === '1',
      allowNotApplicable: row.dataset.fieldAllowNotApplicable === '1',
      allowMultipleMedia: row.dataset.fieldAllowMultipleMedia === '1',
      allowAnnotation: row.dataset.fieldAllowAnnotation === '1',
      allowMultipleAnswers: row.dataset.fieldAllowMultipleAnswers === '1',
      options: this.parseOptions(row.dataset.fieldOptions),
      rows: this.parseRows(row.dataset.fieldRows),
      isRequired: row.dataset.fieldIsOptional !== '1',
      isLeadField: row.dataset.fieldIsLeadField === '1',
      conditionParentFieldId: row.dataset.fieldConditionParentId ?? '',
      conditionAnswer: row.dataset.fieldConditionAnswer ?? ''
    }
  }

  private snapshotBody (snapshot: FieldSnapshot): Record<string, unknown> {
    return {
      name: snapshot.name,
      description: snapshot.description,
      kind: snapshot.kind,
      min: snapshot.min,
      max: snapshot.max,
      maxLength: snapshot.maxLength,
      isDateOnly: snapshot.isDateOnly,
      allowNotApplicable: snapshot.allowNotApplicable,
      allowMultipleMedia: snapshot.allowMultipleMedia,
      allowAnnotation: snapshot.allowAnnotation,
      allowMultipleAnswers: snapshot.allowMultipleAnswers,
      display: snapshot.display,
      options: snapshot.options,
      rows: snapshot.rows,
      isRequired: snapshot.isRequired,
      isLeadField: snapshot.isLeadField,
      conditionParentFieldId: snapshot.conditionParentFieldId,
      conditionAnswer: snapshot.conditionAnswer
    }
  }

  async removeField (): Promise<void> {
    const fieldId = this.selectedFieldId
    const row = fieldId ? this.findRow(fieldId) : null

    if (!fieldId || !row) {
      return
    }

    if (this.isChildRow(row)) {
      await this.removeChildField(fieldId, row)

      return
    }

    await this.removeTopLevelField(fieldId, row)
  }

  private async removeTopLevelField (fieldId: string, row: HTMLElement): Promise<void> {
    const section = this.sectionOf(row)

    if (!section) {
      return
    }

    const rowsInSection = this.rowsInSection(section)
    const index = rowsInSection.indexOf(row)

    this.clearError()

    const snapshot = this.snapshotOf(row)
    const dependents = this.dependentConditionsOf(fieldId)

    try {
      await fetchWithHandlingLoginRedirect(this.deleteUrlTemplateValue.replace(PLACEHOLDER, fieldId), {
        method: 'DELETE',
        headers: XHR_HEADERS
      })
    } catch {
      this.showError(trans(REMOVE_FIELD_FAILED, {}, 'form-types'))

      return
    }

    row.remove()
    this.clearDependentConditionsInDom(fieldId)

    const remaining = this.rowsInSection(section)
    const next = remaining[index] ?? remaining[remaining.length - 1] ?? this.rows()[0]

    if (next) {
      this.selectRow(next)
    } else {
      this.clearSelection()
    }

    this.record({ kind: 'remove', fieldId, sectionId: section.dataset.sectionId ?? '', index, snapshot, dependents })
  }

  private async removeChildField (fieldId: string, row: HTMLElement): Promise<void> {
    const group = this.groupOf(row)

    if (!group) {
      return
    }

    const groupId = group.dataset.fieldId ?? ''
    const siblings = this.childRows(group)
    const index = siblings.indexOf(row)

    this.clearError()

    const snapshot = this.snapshotOf(row)

    try {
      await fetchWithHandlingLoginRedirect(this.deleteUrlTemplateValue.replace(PLACEHOLDER, fieldId), {
        method: 'DELETE',
        headers: XHR_HEADERS
      })
    } catch {
      this.showError(trans(REMOVE_FIELD_FAILED, {}, 'form-types'))

      return
    }

    row.remove()
    this.updateGroupSubtitle(group, this.childRows(group).length)

    const remaining = this.childRows(group)
    const next = remaining[index] ?? remaining[remaining.length - 1] ?? group

    this.selectRow(next)

    this.record({ kind: 'removeChild', groupId, fieldId, index, snapshot })
  }

  async removeGroup (): Promise<void> {
    const fieldId = this.selectedFieldId
    const row = fieldId ? this.findRow(fieldId) : null

    if (!fieldId || !row) {
      return
    }

    const children = this.childRows(row)

    if (children.length === 0) {
      await this.removeTopLevelField(fieldId, row)

      return
    }

    const section = this.sectionOf(row)
    const name = row.querySelector('.ft-question__name')?.textContent ?? ''
    const rowsInSection = section ? this.rowsInSection(section) : []
    const index = rowsInSection.indexOf(row)
    const childRecords = children.map(child => ({ fieldId: child.dataset.fieldId ?? '', snapshot: this.snapshotOf(child) }))

    this.pendingRemoveGroup = {
      groupId: fieldId,
      sectionId: section?.dataset.sectionId ?? '',
      name,
      index,
      children: childRecords,
      conditionParentFieldId: row.dataset.fieldConditionParentId ?? '',
      conditionAnswer: row.dataset.fieldConditionAnswer ?? ''
    }

    this.showRemoveGroupModal(name, childRecords.map(record => record.snapshot.name))
  }

  private showRemoveGroupModal (name: string, childNames: string[]): void {
    const modal = document.getElementById(DELETE_GROUP_MODAL_ID)

    if (!modal) {
      return
    }

    const title = modal.querySelector('.modal-title')

    if (title) {
      title.textContent = trans(REMOVE_GROUP_TITLE, { '%name%': name }, 'form-types')
    }

    const points = modal.querySelector('.ft-modal__points')

    if (points) {
      points.innerHTML = ''

      const key = childNames.length === 1 ? REMOVE_GROUP_BULLET_QUESTIONS_ONE : REMOVE_GROUP_BULLET_QUESTIONS_MANY

      points.appendChild(this.pointItem(trans(key, {
        '%count%': String(childNames.length),
        '%names%': childNames.join(', ')
      }, 'form-types')))

      points.appendChild(this.pointItem(trans(REMOVE_GROUP_BULLET_LOSE_REPEAT, {}, 'form-types')))
    }

    $(modal).modal('show')
  }

  async confirmRemoveGroup (): Promise<void> {
    const pending = this.pendingRemoveGroup

    this.pendingRemoveGroup = null

    if (!pending) {
      return
    }

    this.clearError()

    if (!await this.deleteField(pending.groupId)) {
      this.showError(trans(REMOVE_GROUP_FAILED, {}, 'form-types'))

      return
    }

    if (this.selectedFieldId === pending.groupId) {
      this.clearSelection()
    }

    this.record({
      kind: 'removeGroup',
      groupId: pending.groupId,
      sectionId: pending.sectionId,
      name: pending.name,
      index: pending.index,
      children: pending.children,
      conditionParentFieldId: pending.conditionParentFieldId,
      conditionAnswer: pending.conditionAnswer
    })
  }

  private parseOptions (raw: string | undefined): string[] {
    if (!raw) {
      return []
    }

    try {
      const parsed: unknown = JSON.parse(raw)

      return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : []
    } catch {
      return []
    }
  }

  private currentOptionValues (): string[] {
    return Array.from(this.propertiesOptionsListTarget.querySelectorAll('.ft-optionrow__input'))
      .map(input => (input as HTMLInputElement).value)
  }

  private renderOptions (values: string[]): void {
    this.propertiesOptionsListTarget.innerHTML = ''
    values.forEach(value => this.appendOptionRow(value))
  }

  private appendOptionRow (value: string): HTMLInputElement | null {
    const fragment = this.optionRowTemplateTarget.content.cloneNode(true) as DocumentFragment
    const input = fragment.querySelector('.ft-optionrow__input')

    if (input instanceof HTMLInputElement) {
      input.value = value
    }

    this.propertiesOptionsListTarget.appendChild(fragment)

    return input instanceof HTMLInputElement ? input : null
  }

  private parseRows (raw: string | undefined): string[] {
    if (!raw) {
      return []
    }

    try {
      const parsed: unknown = JSON.parse(raw)

      return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : []
    } catch {
      return []
    }
  }

  private currentRowValues (): string[] {
    return Array.from(this.propertiesRowsListTarget.querySelectorAll('.ft-optionrow__input'))
      .map(input => (input as HTMLInputElement).value)
  }

  private renderRows (values: string[]): void {
    this.propertiesRowsListTarget.innerHTML = ''
    values.forEach(value => this.appendRowRow(value))
  }

  private appendRowRow (value: string): HTMLInputElement | null {
    const fragment = this.rowRowTemplateTarget.content.cloneNode(true) as DocumentFragment
    const input = fragment.querySelector('.ft-optionrow__input')

    if (input instanceof HTMLInputElement) {
      input.value = value
    }

    this.propertiesRowsListTarget.appendChild(fragment)

    return input instanceof HTMLInputElement ? input : null
  }

  private buildRow (field: AddedField, isChild = false): HTMLElement | null {
    const template = field.type === GROUP_TYPE
      ? this.groupRowTemplateTarget
      : (isChild ? this.childRowTemplateTarget : this.rowTemplateTarget)

    const fragment = template.content.cloneNode(true) as DocumentFragment
    const row = fragment.querySelector(QUESTION_SELECTOR)

    if (!(row instanceof HTMLElement)) {
      return null
    }

    row.dataset.fieldId = field.id
    row.dataset.fieldDescription = field.description ?? ''
    row.dataset.fieldType = field.type ?? ''
    row.dataset.fieldKind = field.kind ?? ''
    row.dataset.fieldDisplay = field.display ?? ''
    row.dataset.fieldMin = field.min === null || field.min === undefined ? '' : String(field.min)
    row.dataset.fieldMax = field.max === null || field.max === undefined ? '' : String(field.max)
    row.dataset.fieldMaxLength = field.max_length === null || field.max_length === undefined ? '' : String(field.max_length)
    row.dataset.fieldIsDateOnly = field.is_date_only ? '1' : ''
    row.dataset.fieldAllowNotApplicable = field.allow_not_applicable ? '1' : ''
    row.dataset.fieldAllowMultipleMedia = field.allow_multiple_media ? '1' : ''
    row.dataset.fieldAllowAnnotation = field.allow_annotation ? '1' : ''
    row.dataset.fieldAllowMultipleAnswers = field.allow_multiple_answers ? '1' : ''
    row.dataset.fieldOptions = JSON.stringify(field.options ?? [])
    row.dataset.fieldRows = JSON.stringify(field.rows ?? [])
    row.dataset.fieldIsOptional = field.is_optional ? '1' : ''
    row.dataset.fieldIsLeadField = field.is_lead_field ? '1' : ''
    row.dataset.fieldConditionParentId = field.condition_parent_field_id ?? ''
    row.dataset.fieldConditionAnswer = field.condition_answer ?? ''
    this.updateConditionalTag(row, Boolean(field.condition_parent_field_id))
    this.applyGluedState(row, Boolean(field.condition_parent_field_id))
    row.classList.toggle('ft-question--break', field.type === PAGE_BREAK_TYPE)

    const name = row.querySelector('.ft-question__name')

    if (name) {
      name.textContent = field.name
    }

    if (field.type === GROUP_TYPE) {
      this.updateGroupSubtitle(row, field.children?.length ?? 0)

      const body = row.querySelector<HTMLElement>(GROUP_BODY_SELECTOR)

      if (body) {
        body.dataset.groupId = field.id

        const addRow = body.querySelector('.ft-addrow')

        ;(field.children ?? []).forEach(child => {
          const childRow = this.buildRow(child, true)

          if (!childRow) {
            return
          }

          if (addRow) {
            addRow.before(childRow)
          } else {
            body.appendChild(childRow)
          }
        })
      }

      const addButton = row.querySelector<HTMLElement>('.ft-group__body .ft-addrow button')

      if (addButton) {
        addButton.dataset.groupId = field.id
      }

      return row
    }

    this.updateSummary(row, field)
    this.updateChips(row, field.is_optional ?? false, field.is_lead_field ?? false)

    const icon = row.querySelector('.ft-question__icon')
    const typeLabel = row.querySelector('.ft-question__type')

    if (typeLabel) {
      typeLabel.textContent = field.type_label
    }

    if (icon) {
      icon.className = `fas ${field.icon} ft-question__icon`
    }

    return row
  }

  private buildSectionRow (section: AddedSection): HTMLElement | null {
    const fragment = this.sectionRowTemplateTarget.content.cloneNode(true) as DocumentFragment
    const row = fragment.querySelector(SECTION_SELECTOR)

    if (!(row instanceof HTMLElement)) {
      return null
    }

    row.dataset.sectionId = section.id

    const name = row.querySelector('.ft-section__name')

    if (name) {
      name.textContent = section.name
    }

    const nameInput = row.querySelector('.ft-section__name-edit')

    if (nameInput instanceof HTMLInputElement) {
      nameInput.value = section.name
    }

    this.applySectionConditionResult(row, section.condition_parent_field_id ?? null, section.condition_answer ?? null)

    return row
  }

  private appendRowToSection (section: HTMLElement, field: AddedField): HTMLElement | null {
    const row = this.buildRow(field)

    if (!row) {
      return null
    }

    section.querySelector('.ft-questions')?.appendChild(row)

    return row
  }

  private insertRowAfter (afterRow: HTMLElement, field: AddedField, isChild = false): HTMLElement | null {
    const row = this.buildRow(field, isChild)

    if (!row) {
      return null
    }

    afterRow.after(row)

    const group = isChild ? this.groupOf(row) : null

    if (group) {
      this.updateGroupSubtitle(group, this.childRows(group).length)
    }

    return row
  }

  /**
   * Places a restored field with a condition right after its parent's block, wherever that parent
   * currently is (possibly a different section than the field was originally deleted from), matching
   * the repositioning the backend already performed for the same restore.
   */
  private insertRestoredConditionalRow (field: AddedField): HTMLElement | null {
    const parentRow = field.condition_parent_field_id ? this.findRow(field.condition_parent_field_id) : null

    if (!parentRow) {
      return null
    }

    const row = this.buildRow(field)

    if (!row) {
      return null
    }

    this.conditionBlockEndOf(parentRow, []).after(row)

    return row
  }

  private insertRowAt (section: HTMLElement, index: number, field: AddedField): HTMLElement | null {
    const row = this.buildRow(field)

    if (!row) {
      return null
    }

    const reference = this.rowsInSection(section)[index]

    if (reference) {
      reference.before(row)
    } else {
      section.querySelector('.ft-questions')?.appendChild(row)
    }

    return row
  }

  private async reorder (section: HTMLElement, move: () => void, focusEvent?: Event): Promise<void> {
    const before = this.rowsInSection(section)
    const beforeIds = before.map(row => row.dataset.fieldId ?? '')
    const sectionId = section.dataset.sectionId ?? ''

    this.clearError()
    move()
    this.clearDropTargets()

    if (focusEvent?.currentTarget instanceof HTMLElement) {
      focusEvent.currentTarget.focus()
    }

    const afterIds = this.rowsInSection(section).map(row => row.dataset.fieldId ?? '')

    try {
      await fetchWithHandlingLoginRedirect(this.reorderFieldsUrlTemplateValue.replace(PLACEHOLDER, sectionId), {
        method: 'POST',
        headers: { ...XHR_HEADERS, 'Content-Type': 'application/json' },
        body: JSON.stringify({ field_ids: afterIds })
      })

      this.record({ kind: 'reorder', sectionId, before: beforeIds, after: afterIds })
    } catch {
      const list = section.querySelector('.ft-questions')
      before.forEach(row => list?.appendChild(row))
      this.showError(trans(REORDER_FAILED, {}, 'form-types'))
    }
  }

  /**
   * Moves a dragged question (and anything conditionally glued to it) into a different section, for
   * cross-section drag-and-drop. Persists via moveFields() rather than reorderFields() — the target
   * section doesn't already contain these fields, so the set-equality reorderFields() relies on
   * wouldn't hold — then records a single undoable operation covering both sections' change.
   */
  private async moveAcrossSections (draggedBlock: HTMLElement[], targetSection: HTMLElement, index: number): Promise<void> {
    const sourceSection = this.sectionOf(draggedBlock[0])

    if (!sourceSection) {
      return
    }

    this.clearError()

    const sourceIndex = this.rowsInSection(sourceSection).indexOf(draggedBlock[0])
    const sourceSectionId = sourceSection.dataset.sectionId ?? ''
    const destSectionId = targetSection.dataset.sectionId ?? ''
    const fieldIds = draggedBlock.map(row => row.dataset.fieldId ?? '')

    const moved = await this.applyMove(fieldIds, destSectionId, index)

    if (!moved) {
      this.showError(trans(MOVE_FIELD_FAILED, {}, 'form-types'))

      return
    }

    this.record({ kind: 'moveField', fieldIds, sourceSectionId, sourceIndex, destSectionId, destIndex: index })
  }

  /**
   * The shared primitive behind a drag-triggered cross-section move and its undo/redo: relocates the
   * given rows into the target section at the given index, persists it, and reverts the DOM if the
   * request fails — mirroring applyOrder()'s role for same-section reordering.
   */
  private async applyMove (fieldIds: string[], targetSectionId: string, index: number): Promise<boolean> {
    const targetSection = this.sections().find(candidate => candidate.dataset.sectionId === targetSectionId)
    const rows = fieldIds.map(fieldId => this.findRow(fieldId)).filter((row): row is HTMLElement => row !== null)

    if (!targetSection || rows.length !== fieldIds.length) {
      return false
    }

    const previousSection = this.sectionOf(rows[0])
    const previousIndex = previousSection ? this.rowsInSection(previousSection).indexOf(rows[0]) : -1

    this.placeRowsInSection(targetSection, rows, index)
    this.clearDropTargets()

    try {
      await fetchWithHandlingLoginRedirect(this.moveFieldsUrlTemplateValue.replace(PLACEHOLDER, targetSectionId), {
        method: 'POST',
        headers: { ...XHR_HEADERS, 'Content-Type': 'application/json' },
        body: JSON.stringify({ field_ids: fieldIds, index })
      })

      return true
    } catch {
      if (previousSection) {
        this.placeRowsInSection(previousSection, rows, previousIndex)
      }

      return false
    }
  }

  private placeRowsInSection (section: HTMLElement, rows: HTMLElement[], index: number): void {
    const list = section.querySelector('.ft-questions')
    const siblings = this.rowsInSection(section).filter(row => !rows.includes(row))
    const reference = siblings[Math.max(0, index)]

    if (reference) {
      rows.forEach(row => reference.before(row))
    } else {
      rows.forEach(row => list?.appendChild(row))
    }
  }

  async undo (): Promise<void> {
    const operation = this.done.pop()

    if (!operation) {
      return
    }

    this.clearError()

    switch (operation.kind) {
      case 'reorder': {
        if (!await this.applyOrder(operation.sectionId, operation.before)) {
          this.done.push(operation)
          this.showError(trans(UNDO_FAILED, {}, 'form-types'))
          this.announceChange()

          return
        }

        this.undone.push(operation)
        this.announceChange()

        return
      }
      case 'moveField': {
        if (!await this.applyMove(operation.fieldIds, operation.sourceSectionId, operation.sourceIndex)) {
          this.done.push(operation)
          this.showError(trans(UNDO_FAILED, {}, 'form-types'))
          this.announceChange()

          return
        }

        this.undone.push(operation)
        this.announceChange()

        return
      }
      case 'add':
      case 'duplicate': {
        if (!await this.deleteField(operation.fieldId)) {
          this.done.push(operation)
          this.showError(trans(UNDO_FAILED, {}, 'form-types'))
          this.announceChange()

          return
        }

        this.undone.push(operation)
        this.announceChange()

        return
      }
      case 'remove': {
        const restoredId = await this.restoreField(operation.fieldId, operation.sectionId, operation.snapshot, operation.index, operation.dependents)

        if (!restoredId) {
          this.done.push(operation)
          this.showError(trans(UNDO_FAILED, {}, 'form-types'))
          this.announceChange()

          return
        }

        this.undone.push({ ...operation, fieldId: restoredId })
        this.announceChange()

        return
      }
      case 'addSection': {
        if (!await this.deleteSectionById(operation.sectionId)) {
          this.done.push(operation)
          this.showError(trans(UNDO_FAILED, {}, 'form-types'))
          this.announceChange()

          return
        }

        this.undone.push(operation)
        this.announceChange()

        return
      }
      case 'removeSection': {
        const restored = await this.restoreSectionWithFields(operation.sectionId, operation.name, operation.index, operation.fields, operation.conditionParentFieldId, operation.conditionAnswer, operation.dependents)

        if (!restored) {
          this.done.push(operation)
          this.showError(trans(UNDO_FAILED, {}, 'form-types'))
          this.announceChange()

          return
        }

        const row = this.buildSectionRow(restored)

        if (row) {
          this.placeSectionRowAt(row, operation.index)
          this.replaceSectionFields(row, restored.fields)
        }

        this.applyDependentResults(restored.dependents ?? [])

        this.undone.push({ ...operation, sectionId: restored.id })
        this.announceChange()

        return
      }
      case 'mergeSection': {
        const restored = await this.unmergeSection(operation.sectionId, operation.name, operation.index, operation.fieldIds)

        if (!restored) {
          this.done.push(operation)
          this.showError(trans(UNDO_FAILED, {}, 'form-types'))
          this.announceChange()

          return
        }

        this.removeRowsByIds(operation.fieldIds)

        const row = this.buildSectionRow(restored)

        if (row) {
          this.placeSectionRowAt(row, operation.index)
          this.replaceSectionFields(row, restored.fields)
        }

        this.undone.push({ ...operation, sectionId: restored.id })
        this.announceChange()

        return
      }
      case 'removeGroup': {
        const restored = await this.restoreGroupWithChildren(operation.groupId, operation.sectionId, operation.name, operation.index, operation.children, operation.conditionParentFieldId, operation.conditionAnswer)

        if (!restored) {
          this.done.push(operation)
          this.showError(trans(UNDO_FAILED, {}, 'form-types'))
          this.announceChange()

          return
        }

        const section = this.sections().find(candidate => candidate.dataset.sectionId === operation.sectionId)

        if (restored.condition_parent_field_id) {
          this.insertRestoredConditionalRow(restored)
        } else if (section) {
          this.insertRowAt(section, operation.index, restored)
        }

        this.undone.push({ ...operation, groupId: restored.id })
        this.announceChange()

        return
      }
      case 'addChild': {
        if (!await this.deleteField(operation.fieldId)) {
          this.done.push(operation)
          this.showError(trans(UNDO_FAILED, {}, 'form-types'))
          this.announceChange()

          return
        }

        this.undone.push(operation)
        this.announceChange()

        return
      }
      case 'removeChild': {
        const restoredId = await this.restoreChildField(operation.fieldId, operation.groupId, operation.snapshot, operation.index)

        if (!restoredId) {
          this.done.push(operation)
          this.showError(trans(UNDO_FAILED, {}, 'form-types'))
          this.announceChange()

          return
        }

        this.undone.push({ ...operation, fieldId: restoredId })
        this.announceChange()

        return
      }
      case 'reorderChildren': {
        if (!await this.applyChildOrder(operation.groupId, operation.before)) {
          this.done.push(operation)
          this.showError(trans(UNDO_FAILED, {}, 'form-types'))
          this.announceChange()

          return
        }

        this.undone.push(operation)
        this.announceChange()

        return
      }
      case 'condition': {
        if (!await this.applyConditionState(operation.fieldId, operation.before)) {
          this.done.push(operation)
          this.showError(trans(UNDO_FAILED, {}, 'form-types'))
          this.announceChange()

          return
        }

        this.undone.push(operation)
        this.announceChange()
      }
    }
  }

  async redo (): Promise<void> {
    const operation = this.undone.pop()

    if (!operation) {
      return
    }

    this.clearError()

    switch (operation.kind) {
      case 'reorder': {
        if (!await this.applyOrder(operation.sectionId, operation.after)) {
          this.undone.push(operation)
          this.showError(trans(REDO_FAILED, {}, 'form-types'))
          this.announceChange()

          return
        }

        this.done.push(operation)
        this.announceChange()

        return
      }
      case 'moveField': {
        if (!await this.applyMove(operation.fieldIds, operation.destSectionId, operation.destIndex)) {
          this.undone.push(operation)
          this.showError(trans(REDO_FAILED, {}, 'form-types'))
          this.announceChange()

          return
        }

        this.done.push(operation)
        this.announceChange()

        return
      }
      case 'add': {
        const section = this.sections().find(candidate => candidate.dataset.sectionId === operation.sectionId)
        const field = section ? await this.addField(operation.type, operation.sectionId, '', operation.fieldId) : null

        if (!section || !field) {
          this.undone.push(operation)
          this.showError(trans(REDO_FAILED, {}, 'form-types'))
          this.announceChange()

          return
        }

        this.appendRowToSection(section, field)
        this.done.push({ ...operation, fieldId: field.id })
        this.announceChange()

        return
      }
      case 'duplicate': {
        const newFieldId = await this.duplicateFrom(operation.sourceFieldId, operation.fieldId)

        if (!newFieldId) {
          this.undone.push(operation)
          this.showError(trans(REDO_FAILED, {}, 'form-types'))
          this.announceChange()

          return
        }

        this.done.push({ ...operation, fieldId: newFieldId })
        this.announceChange()

        return
      }
      case 'remove': {
        if (!await this.deleteField(operation.fieldId)) {
          this.undone.push(operation)
          this.showError(trans(REDO_FAILED, {}, 'form-types'))
          this.announceChange()

          return
        }

        this.done.push(operation)
        this.announceChange()

        return
      }
      case 'addSection': {
        const section = await this.createSection(operation.sectionId)

        if (!section) {
          this.undone.push(operation)
          this.showError(trans(REDO_FAILED, {}, 'form-types'))
          this.announceChange()

          return
        }

        const row = this.buildSectionRow(section)

        if (row) {
          this.sectionListTarget.appendChild(row)
        }

        this.done.push({ ...operation, sectionId: section.id })
        this.announceChange()

        return
      }
      case 'removeSection': {
        if (!await this.deleteSectionById(operation.sectionId)) {
          this.undone.push(operation)
          this.showError(trans(REDO_FAILED, {}, 'form-types'))
          this.announceChange()

          return
        }

        this.done.push(operation)
        this.announceChange()

        return
      }
      case 'mergeSection': {
        const section = this.sections().find(candidate => candidate.dataset.sectionId === operation.sectionId)
        const above = this.sections().find(candidate => candidate.dataset.sectionId === operation.aboveSectionId)
        const merged = section && above ? await this.mergeSection(operation.sectionId) : null

        if (!section || !above || !merged) {
          this.undone.push(operation)
          this.showError(trans(REDO_FAILED, {}, 'form-types'))
          this.announceChange()

          return
        }

        this.replaceSectionFields(above, merged.fields)
        section.remove()

        if (this.selectedSectionId === operation.sectionId) {
          this.clearSelection()
        }

        this.done.push(operation)
        this.announceChange()

        return
      }
      case 'removeGroup': {
        if (!await this.deleteField(operation.groupId)) {
          this.undone.push(operation)
          this.showError(trans(REDO_FAILED, {}, 'form-types'))
          this.announceChange()

          return
        }

        if (this.selectedFieldId === operation.groupId) {
          this.clearSelection()
        }

        this.done.push(operation)
        this.announceChange()

        return
      }
      case 'addChild': {
        const field = await this.addChildFieldRequest(operation.groupId, operation.type, operation.fieldId)

        if (!field) {
          this.undone.push(operation)
          this.showError(trans(REDO_FAILED, {}, 'form-types'))
          this.announceChange()

          return
        }

        this.appendChildRow(operation.groupId, field)
        this.done.push({ ...operation, fieldId: field.id })
        this.announceChange()

        return
      }
      case 'removeChild': {
        if (!await this.deleteField(operation.fieldId)) {
          this.undone.push(operation)
          this.showError(trans(REDO_FAILED, {}, 'form-types'))
          this.announceChange()

          return
        }

        this.done.push(operation)
        this.announceChange()

        return
      }
      case 'reorderChildren': {
        if (!await this.applyChildOrder(operation.groupId, operation.after)) {
          this.undone.push(operation)
          this.showError(trans(REDO_FAILED, {}, 'form-types'))
          this.announceChange()

          return
        }

        this.done.push(operation)
        this.announceChange()

        return
      }
      case 'condition': {
        if (!await this.applyConditionState(operation.fieldId, operation.after)) {
          this.undone.push(operation)
          this.showError(trans(REDO_FAILED, {}, 'form-types'))
          this.announceChange()

          return
        }

        this.done.push(operation)
        this.announceChange()
      }
    }
  }

  private async addField (type: string, sectionId: string, display: string = '', id?: string): Promise<AddedField | null> {
    try {
      const response = await fetchWithHandlingLoginRedirect(
        this.addFieldUrlTemplateValue.replace(PLACEHOLDER, sectionId),
        {
          method: 'POST',
          headers: { ...XHR_HEADERS, 'Content-Type': 'application/json' },
          body: JSON.stringify({ type, display, id })
        }
      )

      const body = await response.json() as AddFieldResponse

      return body.result.field
    } catch {
      return null
    }
  }

  private async restoreField (fieldId: string, sectionId: string, snapshot: FieldSnapshot, index: number, dependents: DependentCondition[]): Promise<string | null> {
    const section = this.sections().find(candidate => candidate.dataset.sectionId === sectionId)

    if (!section) {
      return null
    }

    let restored: RestoreFieldResponse['result']

    try {
      const response = await fetchWithHandlingLoginRedirect(this.restoreFieldUrlTemplateValue.replace(PLACEHOLDER, sectionId), {
        method: 'POST',
        headers: { ...XHR_HEADERS, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: fieldId,
          index,
          type: snapshot.type,
          ...this.snapshotBody(snapshot),
          dependents: dependents.map(dependent => this.dependentBody(dependent))
        })
      })

      restored = (await response.json() as RestoreFieldResponse).result
    } catch {
      return null
    }

    const field = restored.field

    if (!(field.condition_parent_field_id ? this.insertRestoredConditionalRow(field) : null)) {
      this.insertRowAt(section, index, field)
    }

    this.applyDependentResults(restored.dependents)

    return field.id
  }

  private async deleteField (fieldId: string): Promise<boolean> {
    try {
      await fetchWithHandlingLoginRedirect(this.deleteUrlTemplateValue.replace(PLACEHOLDER, fieldId), {
        method: 'DELETE',
        headers: XHR_HEADERS
      })
    } catch {
      return false
    }

    const row = this.findRow(fieldId)
    const group = row ? this.groupOf(row) : null

    row?.remove()

    if (group) {
      this.updateGroupSubtitle(group, this.childRows(group).length)
    }

    this.clearDependentConditionsInDom(fieldId)

    return true
  }

  private async applyOrder (sectionId: string, fieldIds: string[]): Promise<boolean> {
    const section = this.sections().find(candidate => candidate.dataset.sectionId === sectionId)

    if (!section) {
      return false
    }

    const previous = this.rowsInSection(section).map(row => row.dataset.fieldId ?? '')

    this.applyOrderToRows(section, fieldIds)

    try {
      await fetchWithHandlingLoginRedirect(this.reorderFieldsUrlTemplateValue.replace(PLACEHOLDER, sectionId), {
        method: 'POST',
        headers: { ...XHR_HEADERS, 'Content-Type': 'application/json' },
        body: JSON.stringify({ field_ids: fieldIds })
      })
    } catch {
      this.applyOrderToRows(section, previous)

      return false
    }

    return true
  }

  private applyOrderToRows (section: HTMLElement, fieldIds: string[]): void {
    const list = section.querySelector('.ft-questions')

    fieldIds.forEach(fieldId => {
      const row = this.rowsInSection(section).find(candidate => candidate.dataset.fieldId === fieldId)

      if (row) {
        list?.appendChild(row)
      }
    })
  }

  private record (operation: Operation): void {
    this.done.push(operation)
    this.undone = []
    this.announceChange()
  }

  private announceChange (): void {
    this.refreshConditionDepths()
    this.dispatch('changed', {
      target: window,
      prefix: 'form-builder',
      detail: { canUndo: this.done.length > 0, canRedo: this.undone.length > 0 }
    })
  }

  private rowFrom (event: Event): HTMLElement | null {
    const target = event.currentTarget as HTMLElement

    return target.closest(QUESTION_SELECTOR)
  }

  private sectionFrom (event: Event): HTMLElement | null {
    const target = event.currentTarget as HTMLElement

    return target.closest(SECTION_SELECTOR)
  }

  private sectionOf (row: HTMLElement): HTMLElement | null {
    return row.closest(SECTION_SELECTOR)
  }

  private lastSection (): HTMLElement | null {
    const sections = this.sections()

    return sections[sections.length - 1] ?? null
  }

  /**
   * A palette add lands right after whatever is currently selected — the selected question (and
   * anything glued to it, so it never lands mid-block), or a question inside a repeating group
   * resolves to right after the group itself, since a palette add can never go inside a group — or at
   * the end of the selected section when a section rather than a question is selected. Falls back to
   * the end of the last section only when nothing is selected at all. Matches Google Forms/Jotform:
   * always appending to the very end regardless of what the user was just looking at was a frequent
   * surprise on a form with more than one section.
   */
  private insertTargetForAdd (): { section: HTMLElement, anchorRow: HTMLElement | null } | null {
    if (this.selectedFieldId) {
      const row = this.findRow(this.selectedFieldId)
      const anchorTarget = row && this.isChildRow(row) ? this.groupOf(row) : row

      if (anchorTarget) {
        const block = this.blockOf(anchorTarget)
        const anchor = block[block.length - 1]
        const section = this.sectionOf(anchor)

        if (section) {
          return { section, anchorRow: anchor }
        }
      }
    }

    if (this.selectedSectionId) {
      const section = this.sections().find(candidate => candidate.dataset.sectionId === this.selectedSectionId)

      if (section) {
        return { section, anchorRow: null }
      }
    }

    const section = this.lastSection()

    return section ? { section, anchorRow: null } : null
  }

  /**
   * addField() always creates the new field at the end of its section server-side; when the row also
   * needs to land at a specific spot within that section (right after the current selection), this
   * persists the resulting visual order via the same reorder endpoint drag-and-drop already uses —
   * silently, with no separate undo step, since undoing the add's own record() already deletes the
   * field regardless of where it ended up positioned.
   */
  private async persistFieldOrder (section: HTMLElement): Promise<boolean> {
    const sectionId = section.dataset.sectionId ?? ''
    const fieldIds = this.rowsInSection(section).map(row => row.dataset.fieldId ?? '')

    try {
      await fetchWithHandlingLoginRedirect(this.reorderFieldsUrlTemplateValue.replace(PLACEHOLDER, sectionId), {
        method: 'POST',
        headers: { ...XHR_HEADERS, 'Content-Type': 'application/json' },
        body: JSON.stringify({ field_ids: fieldIds })
      })

      return true
    } catch {
      return false
    }
  }

  private sections (): HTMLElement[] {
    return Array.from(this.sectionListTarget.querySelectorAll<HTMLElement>(SECTION_SELECTOR))
  }

  private rows (): HTMLElement[] {
    return this.allRows().filter(row => !this.isChildRow(row))
  }

  private rowsInSection (section: HTMLElement): HTMLElement[] {
    return Array.from(section.querySelectorAll<HTMLElement>(QUESTION_SELECTOR)).filter(row => !this.isChildRow(row))
  }

  private allRows (): HTMLElement[] {
    return Array.from(this.sectionListTarget.querySelectorAll<HTMLElement>(QUESTION_SELECTOR))
  }

  private childRows (group: HTMLElement): HTMLElement[] {
    const body = group.querySelector<HTMLElement>(GROUP_BODY_SELECTOR)

    return body ? Array.from(body.querySelectorAll<HTMLElement>(QUESTION_SELECTOR)) : []
  }

  private isChildRow (row: HTMLElement): boolean {
    return row.closest(GROUP_BODY_SELECTOR) !== null
  }

  private groupOf (row: HTMLElement): HTMLElement | null {
    const body = row.closest(GROUP_BODY_SELECTOR)

    return body ? body.closest<HTMLElement>(QUESTION_SELECTOR) : null
  }

  private findRow (fieldId: string): HTMLElement | null {
    return this.sectionListTarget.querySelector<HTMLElement>(`${QUESTION_SELECTOR}[data-field-id="${fieldId}"]`)
  }

  /**
   * A glued row is shown only when its condition's answer matches, so it sits fixed immediately after
   * the question that controls it and cannot be moved independently — only the block it belongs to
   * (its controlling question and everything else glued to it) moves as a unit.
   */
  private isGluedRow (row: HTMLElement): boolean {
    return !this.isChildRow(row) && Boolean(row.dataset.fieldConditionParentId)
  }

  private isGluedUnder (row: HTMLElement, blockRootId: string): boolean {
    let current: HTMLElement | null = row
    let hops = 0

    while (current?.dataset.fieldConditionParentId && hops++ < 10) {
      if (current.dataset.fieldConditionParentId === blockRootId) {
        return true
      }

      current = this.findRow(current.dataset.fieldConditionParentId)
    }

    return false
  }

  /**
   * The contiguous block a row belongs to: itself plus every row immediately following it that is
   * glued to it, directly or transitively (a two-level condition chain moves as one unit).
   */
  private blockOf (row: HTMLElement): HTMLElement[] {
    const block = [row]
    const rowId = row.dataset.fieldId ?? ''
    let sibling = row.nextElementSibling

    while (sibling instanceof HTMLElement && this.isGluedUnder(sibling, rowId)) {
      block.push(sibling)
      sibling = sibling.nextElementSibling
    }

    return block
  }

  private blockStartOf (row: HTMLElement): HTMLElement {
    let current = row

    while (current.dataset.fieldConditionParentId) {
      const parentRow = this.findRow(current.dataset.fieldConditionParentId)

      if (!parentRow) {
        return current
      }

      current = parentRow
    }

    return current
  }

  private conditionBlockEndOf (parentRow: HTMLElement, excluding: HTMLElement[]): HTMLElement {
    const parentId = parentRow.dataset.fieldId ?? ''
    let end = parentRow
    let sibling = parentRow.nextElementSibling

    while (sibling instanceof HTMLElement && (excluding.includes(sibling) || this.isGluedUnder(sibling, parentId))) {
      if (!excluding.includes(sibling)) {
        end = sibling
      }

      sibling = sibling.nextElementSibling
    }

    return end
  }

  private blockEndOf (row: HTMLElement): HTMLElement {
    const block = this.blockOf(this.blockStartOf(row))

    return block[block.length - 1]
  }

  private markDropTarget (row: HTMLElement): void {
    row.classList.add(DROP_TARGET_CLASS)
  }

  private clearDropTargets (): void {
    this.rows().forEach(row => row.classList.remove(DROP_TARGET_CLASS))
    this.sections().forEach(section => section.querySelector('.ft-addrow')?.classList.remove(ADD_ROW_DROP_TARGET_CLASS))
  }

  private showError (message: string): void {
    if (!this.hasErrorTarget) {
      return
    }

    this.errorTarget.textContent = message
    this.errorTarget.classList.remove('d-none')
  }

  private clearError (): void {
    if (!this.hasErrorTarget) {
      return
    }

    this.errorTarget.textContent = ''
    this.errorTarget.classList.add('d-none')
  }
}
